import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addPartChild, bindDrawingPartMaster, bulkDeletePartMaster, bulkSetPartFolder,
  createFolder, createPartMaster, deleteFolder, deletePartMaster, exportPartMaster,
  fetchBomChildren, fetchBomParents, fetchDrawingTexts, fetchPartCustomers,
  fetchPartDrawings, getFolderTree, getPartMaster,
  listDrawings,
  listPartChildren, listPartMaster, listPartMasterDrawings, listPartMasterPage,
  listPartParents,
  patchFolder, removeBomEdge, savePartFields, setPartFolder, setPartNoOverride,
  updateBomEdge, updatePartMaster,
} from '../api/client';
import { BatchExportDialog } from './BatchExportDialog';
import type {
  BomEdge, DrawingScope, DrawingSummary, FolderNode, FolderTree, PartMasterRow,
  PartMasterScope, PartMasterUpsert, SipEntry,
} from '../types';
import { invalidateDrawingGraph } from '../lib/graphRefresh';
// v0.61：客户 / 项目类型 / 模具类型 / 模穴 / 冲床吨位改由后台清单驱动（严格下拉）。
import { OptionSelect } from './OptionSelect';
import { useOptionValues } from '../store/options';
import {
  PM_ALL_FIELDS, PM_BIZ_FIELDS, PM_FIELD_CN, PM_FIELD_GROUPS, PM_SOURCE_CN,
} from '../types';

/**
 * 「零件主数据」栏目（v0.38）。
 *
 * 界面规范逐条对应工作区 `零件主数据栏目_界面预览_20261009.html` 的 §①②⑥：
 *   · §⑥-1 环境标识（实例/端口/构建模式）**一律不进用户界面** —— 本文件不引用
 *     任何 `import.meta.env` / 端口常量；
 *   · §⑥-2 术语**全链路统一「表面处理」**（列头 / 表单 / 提示 / 导出都由
 *     `PM_FIELD_CN` 出，不写字面量）；
 *   · §⑥-3 批量操作：行首勾选 + 表头全选（含半选态）+ 导出所选 / 批量删除
 *     （二次确认并列出将删条数）。
 *     ⚠️ 「批量改客户」已于 v0.39 **按用户要求删除**（前后端一并删干净，不留孤儿接口）；
 *        要改客户就在列表里点开那一行逐行改。
 *   · v0.39 新增「图纸」列：显示该行已归户的图纸张数，0 ⇒ 灰标「无图纸」。
 *     用户 2026-10-10：「图纸目录删除后主数据也消失」→ 实际选的是
 *     「不删行，只解除并标注」⇒ 这个标注就落在这一列上。
 *   · v0.41 新增「BOM」列（用户 2026-10-11：「BOM那里需要优化多级，总成、
 *     总成下面还有子零件」）：`child_count > 0` ⇒ 这行是**总成**，可点开看子件；
 *     `parent_count > 1` ⇒ 它是**共用件**（同一零件装在多个总成下）。
 *     ★ 层级用**行内展开**表达，**绝不复制行**：共用件会同时挂在几个总成下，
 *       真按树复制的话「一行 = 一个台账行」这个前提就没了，勾选 / 批量删除 /
 *       导出会全部错乱（它们都按 `id` 走）。
 *     ★ 展开是**惰性**的：点开才请求 `/children`，列表页不为每行预先拉子件。
 *   · v0.42 按用户 2026-10-11 的三条口径改造：
 *     ① **单击 / 双击分开**（用户：「单击图纸应该出下子零件数据，双击出现16项
 *        编辑界面」）：单击行 = 展开子零件（BOM），双击行 = 打开 16 项编辑面板。
 *        ⚠️ 浏览器点一下会先发 `click` 再发 `dblclick` ⇒ 单击动作**必须延迟
 *        `ROW_CLICK_DELAY_MS`**，期间同行的第二次点击把它取消掉；否则双击会
 *        「先展开、再弹面板」。去抖状态存 `useRef`（`useState` 是异步的，
 *        第二次点击读到的是旧值，去抖会失效）。
 *     ② **16 项编辑面板居中**（用户：「主数据的16选项界面放到中间来」）——
 *        原来是贴右的抽屉，`.pm-drawer-mask` 改成 flex 居中。
 *        ⚠️ 类名仍叫 `pm-drawer*`（历史名），它描述的是"面板骨架"，不是"靠边"。
 *     ③ 新增「识别到的文字」一栏（用户：「导入图纸识别到的内容需要在主数据界面
 *        体现」，口径：「先把 OCR 原文列给我看」）—— **只摆原文**，
 *        **不自动填** 16 项里任何一格。识别值可能是错的，自动填下去就成了
 *        "有把握的错值"，比留空更危险。见 `RecognizedTextsPanel`。
 *   · v0.44 按用户 2026-10-11 的两条改造：
 *     ① 「能不能将16项内容显示在主数据上」⇒ 列表列从 7 项补到 **16 项全量**
 *        （`COLS` = 常用 7 项前置 + `PM_ALL_FIELDS` 去重补齐，不手写清单），
 *        并加「列」开关（勾掉的列只影响列表视图；导出与编辑面板始终全量）。
 *        ⚠️ 16 列必然超出一屏 ⇒ 表格改 `width: max-content` + 首列 sticky 横向滚动。
 *     ② 「如果没有图纸那我是不是没法建立子零件？手动输入子零件怎么建立」⇒
 *        `PickerParts` 里加「**+ 新建零件并挂上**」：当场建台账行并挂上。
 *        ★ 口径澄清（写进界面文案）：**台账行与图纸是两回事**，
 *        没有图纸的行标「无图纸」是**提示不是错误** —— "没图纸"从来不是障碍。
 *   · v0.45 按用户 2026-10-11 的两条改造（**收窄** v0.44 的 16 列，并给子零件一个显式入口）：
 *     ① 「主数据只体现一下几个内容，并按照这个顺序调整：零件编号、零件名称、客户、
 *        项目号（项目名称变更）、内部编号、模具编号、材料牌号、材料规格、图纸」⇒
 *        列表默认只摆这 **9 列**（`DEFAULT_COLS` 那 8 项内容 + 行级元信息的「图纸」列），
 *        **顺序就是用户给的顺序**；v0.44 摆上来的「图纸版本 / 表面处理」等 8 项
 *        退到「列」开关里（勾一下就回来，**不是删掉**）。
 *        ⚠️ 为此换掉了持久化键（结尾加 `_v2`）：默认值从"全开"变成"9 列"，
 *           沿用旧键的话上一版存下的**空集**会把新默认顶掉 ⇒ 界面看起来根本没改。
 *     ② 「零件编号旁边增加个 + 或者箭头，打开有子零件的信息」⇒ 「零件编号」格左边
 *        加 `+ / −` 展开器（走 `toggleBom`），**原来那个独立的「BOM」列随之撤掉**，
 *        能力全搬过来（展开、共用件的「共用 ×N」）。
 *        「文件夹」列同样撤掉，归属下拉搬进**展开行** —— 撤列可以，撤能力不行。
 *        ⚠️ 展开器必须是**按钮 + `stopPropagation`**：这一行「单击=展开、双击=编辑」
 *           都走去抖，让整格可点的话用户**想双击编辑**时第一下会被算成单击展开。
 *        ⚠️ 「项目名称」改叫「项目号」是**全链路**的事（后端 `FIELD_CN` 是唯一出处，
 *           前端镜像是它的副本，两边一起改）—— 只改列头就会长出"同一字段两个名字"。
 *   · §⑥-4 承载信息的文字对比度 ≥4.5:1（配色见 styles.css 的 `.pm-*` 段）；
 *   · §⑥-5 底部有「取消」，有未保存改动时点 × 先弹确认（保存并关闭 / 放弃 / 继续编辑）；
 *   · §⑥-6 行 hover 整行变灰 + 行尾 › 箭头 + cursor:pointer，且「点任意一行」
 *     的提示条在**表格上方**（`.pm-hintbar`），不在表底。
 *
 * 数据正确性（§④⑤）由后端保证：唯一键四列表达式索引 + 应用层归一查重 +
 * 未填一律 NULL + 手填值锁定（`source='manual'`）。
 */

/** 列表上**固定显示**的字段列（v0.45）。顺序 = 用户给的清单，逐字照排。
 *
 *  用户 2026-10-11 原话：
 *     「主数据只体现一下几个内容，并按照这个顺序调整：零件编号、零件名称、
 *       客户、项目号（项目名称变更）、内部编号、模具编号、材料牌号、材料规格、图纸」
 *
 *  ★ 这里只放**8 项内容**：清单第 9 项「图纸」是**行级元信息**列
 *    （`drawing_count` 不在 16 项里），固定在字段列之后单独渲染，
 *    所以不写进这个数组 —— 写进来会被 `COLS` 的组合逻辑当成字段去查中文名。
 *  ★ 顺序**就是用户给的顺序**，不要"顺手"把别的字段前置。
 *    v0.44 把「图纸版本 / 表面处理」摆在最前两列，本版用户把它们从列表上撤了。
 *  ★ 撤下来的 8 项**不是删掉**：它们仍在 `COLS`（列开关）里，勾一下就回来；
 *    导出与 16 项编辑面板一直是全量。
 */
const DEFAULT_COLS: Array<keyof PartMasterRow> = [
  'part_no', 'part_name', 'customer', 'project_name',
  'internal_no', 'mold_no', 'material_grade', 'material_spec',
];

/** 列表**全集** = `DEFAULT_COLS` 前置 + 其余按唯一出处顺序补齐（= 16 项）。
 *
 *  ⚠️ 中文名一律**从 `PM_FIELD_CN` 取，不写字面量** —— 有字面量就会重新长出
 *     「同一语义两个名字」：第一版这里写死了 `'版本'`，而表单/导出里是
 *     「图纸版本」，同一字段在界面上有两个叫法。这正是本项目
 *     「改了没反应」的头号诊断（同一语义几份实现），也是 §⑥-② 要守的东西。
 *     列顺序仍是界面决策，所以只固定**键**，中文名跟着唯一出处走。
 *
 *  ★ v0.45：`DEFAULT_COLS` 是**默认显示**的那 8 项；这个 `COLS` 是**列开关的
 *     全集**（16 项）。
 *  ★ 写法上**刻意不手写这 16 个键**，而是「`DEFAULT_COLS` + `PM_ALL_FIELDS`
 *     去重补齐」—— 手写清单的隐患是：后端将来加第 17 项时，这里**不会报错、
 *     只会静静地少一列**；现在这样写则自动跟上，且天然不重不漏。
 *
 *  ⚠️ 列多到一定程度就会超出一屏 ⇒ 依赖 `.pm-tablebox` 的横向滚动（它本来就是
 *     `overflow:auto`）。首列（勾选）在 `styles.css` 里做了 `sticky left:0`，
 *     横向滚动时勾选框不会被滚走 —— 否则"滚到右边就多选不了"。
 */
const COLS: Array<{ key: keyof PartMasterRow; label: string }> = [
  ...DEFAULT_COLS,
  ...(PM_ALL_FIELDS as Array<keyof PartMasterRow>)
    .filter((k) => !DEFAULT_COLS.includes(k)),
].map((key) => ({ key, label: PM_FIELD_CN[String(key)] ?? String(key) }));

/** 默认**关掉**的列 = 16 项减去 `DEFAULT_COLS`（v0.45 = 8 项）。
 *
 *  ★ 这是「存隐藏的列」这套机制下表达"默认只显示 9 列"的**唯一**正确方式：
 *    默认值必须是**隐藏集**，而不是"显示的清单"—— 后者会让后端将来的第 17 项
 *    不在旧清单里 ⇒ 永远看不见、且不报错。
 */
const DEFAULT_HIDDEN: ReadonlySet<string> = new Set(
  (PM_ALL_FIELDS as string[]).filter((k) => !(DEFAULT_COLS as string[]).includes(k)));

/** 「被勾掉的列」的 localStorage 键。
 *
 *  ★ 存**隐藏的**、而不是**显示的** —— 这个方向很关键：
 *    存"显示的清单"的话，后端哪天真加了第 17 项，它会因为**不在旧清单里**
 *    而在列表上永远看不见（"加了字段但界面没有"，查起来极费劲）；
 *    存"隐藏的"则新字段默认可见 —— 用户没主动关过它。
 *
 *  ⚠️ v0.45 **换了键名**（结尾加 `_v2`）：默认从"16 项全开"改成"9 列"，
 *     沿用旧键的话，上一版存下的**空集**（= 16 项全开）会把新默认值顶掉
 *     ⇒ 用户升级后界面**看起来根本没改**，而且没有任何报错。
 */
const HIDDEN_COLS_LS_KEY = 'gdt_balloon_pm_hidden_cols_v2';

/** 读回用户勾掉过的列。
 *
 *  ⚠️ **没存过 ⇒ 返回默认隐藏集**，不是空集 —— 空集代表"16 项全开"，
 *     那是 v0.44 的默认。存储异常同样回退到默认隐藏集（宁可少显示，
 *     也不要因为一次 JSON 解析失败就把 16 列糊上屏幕）。
 */
function loadHiddenCols(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_COLS_LS_KEY);
    if (raw == null) return new Set(DEFAULT_HIDDEN);
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr)
      ? arr.filter((x) => typeof x === 'string')
      : [...DEFAULT_HIDDEN]);
  } catch { return new Set(DEFAULT_HIDDEN); }
}

/** 写回（隐私模式等场景 localStorage 会抛，**静默忽略** —— 列显示是偏好不是数据）。 */
function saveHiddenCols(hidden: Set<string>): void {
  try { localStorage.setItem(HIDDEN_COLS_LS_KEY, JSON.stringify([...hidden])); } catch { /* ignore */ }
}

type Toast = (msg: string, type?: 'success' | 'error') => void;

/** 单击行的去抖窗口（ms）—— 见 `rowClick` 的注释。
 *
 *  ★ 必须 < 系统双击间隔，否则用户"双击"里的第一次点击已经先触发了展开。
 *    Windows 默认双击间隔 500ms，取 220ms 留足余量又不显迟滞。
 *  ★ 这里**只吸收"同一行"的第二次点击**：换一行点会立即结掉上一行的单击。
 *    写成"任意两次点击都合并"的话，快速连续展开两行就会漏掉一次。
 */
const ROW_CLICK_DELAY_MS = 220;

/** 行 → 编辑表单（空值统一成空串，方便受控输入）。 */
function toForm(row?: PartMasterRow | null): Record<string, string> {
  const f: Record<string, string> = {};
  for (const k of [...PM_ALL_FIELDS, 'note']) {
    f[k] = ((row as any)?.[k] ?? '') as string;
  }
  return f;
}

/** 表单 → 请求体（空串 → null：**未填就是 NULL，不写空串、不写哨兵**）。 */
function toPayload(form: Record<string, string>): PartMasterUpsert {
  const out: Record<string, string | null> = {};
  for (const k of [...PM_ALL_FIELDS, 'note']) {
    const v = (form[k] ?? '').trim();
    out[k] = v ? v : null;
  }
  return out as unknown as PartMasterUpsert;
}

export function PartMasterPage({ onToast, focusId, onFocusConsumed, focusFolderId,
                                  onFocusFolderConsumed, currentDrawingId,
                                  onOpenDrawings, onOpenSip,
                                  canEdit: canEditProp = true }: {
  onToast: Toast;
  /** 从图纸工作台「在零件主数据里查看」跳进来时，要自动展开的行。 */
  focusId?: number | null;
  onFocusConsumed?: () => void;
  /**
   * v0.56（用户 2026-10-16「三者需要联动」）：从**图纸区**点文件夹上的
   * 「M 个零件」跳进来时，要落到哪个文件夹。
   *
   * ★ 与 `focusId`（展开某一行）是**两件事**，刻意不合并成一个「焦点对象」：
   *   一个是"看某一个零件的详情行"，一个是"看某一个文件夹下的清单"，
   *   合并后每个调用点都要多造一个 `{kind:...}` 字面量，收益为零。
   * ⚠️ 落到文件夹 = 把左侧范围切到 `{kind:'folder', id}` —— 与手点文件夹**同一
   *   条路径**（`setScope`），不另写一套"按文件夹取数"的实现。
   */
  focusFolderId?: number | null;
  onFocusFolderConsumed?: () => void;
  /** 图纸工作台当前打开的图纸 —— 用来在「关联图纸」里标「（当前）」。 */
  currentDrawingId?: number | null;
  /**
   * v0.40（用户 2026-10-10 定顶层结构 `BOM（零件主数据）—— 图纸`）：
   * 从列表**钻取**到图纸区。图纸不再是平级栏目，而是零件的下一层。
   * 传 `{kind:'part'}` = 该零件名下的图纸；`{kind:'unassigned'}` = 还没归户的。
   */
  onOpenDrawings: (scope: Exclude<DrawingScope, { kind: 'all' }>) => void;
  /**
   * v0.50：「SIP」入口（用户 2026-10-14 拍板「3、两个都要」= 零件行 + 图纸行都放）。
   *
   * ★ 只传 `{kind:'part', id}` —— **不在这里查「这个零件有哪些图纸」**：
   *   台账行不绑定图纸（0 张合法、多张常见），"到底打开哪张"由 SIP 页自己解析
   *   （见 `components/sip/SipPage.tsx`）。在这里先查一次再传 drawingId，
   *   等于把「0 张/多张」的分支写在两个入口里 —— 图纸行那份根本用不上。
   */
  onOpenSip?: (e: SipEntry) => void;
  /**
   * v0.47：能否改（新增 / 编辑 16 项 / 删除 / 归户 / BOM 增删 / 文件夹增删）。
   * 只读角色仍然可以：搜索、筛选、列开关、展开看子零件、导出 CSV、双击看详情。
   * ★ 界面层收敛；真正的拦截在后端权限矩阵（`/api/part-master*` 的写方法要求 engineer）。
   */
  canEdit?: boolean;
}) {
  // 🔴 v0.83（2026-10-22 沈哥「物料信息必须保持单一来源」）：本页**整页只读**。
  //
  //   零件/物料的唯一来源是 BOM 平台（本地 `bom_part_ref` 镜像，`sync_bom.py` 单向同步）。
  //   后端 `part_master_routes` 的写方法（POST/PUT/PATCH/DELETE）已全部 **410 Gone**，
  //   但前端还留着「新增零件」按钮和 16 字段编辑弹窗 —— 用户填完整张表点保存才看到
  //   「已废弃」，比没有入口恶劣得多。
  //
  //   ⇒ 把本页的 `canEdit` 钉死成 `false`：所有 `canEdit && (...)` 的写入口
  //     （新增 / 编辑 16 项 / 删除 / 批量删除 / 勾选 / 归户）一次性消失。
  //     将来真要开回某个入口，改这一行即可（不用去翻十几处判断）。
  //
  //   ⚠️ **文件夹树不一起关掉**：文件夹是本地组织方式（不是物料数据），
  //     建/改名/删除与 BOM 无关，所以原样把 `canEditProp` 传下去（第 652 行）。
  //     两者的区别就是「这个事实的唯一出处是谁」——物料归 BOM，文件夹归本系统。
  const canEdit = false;

  const qc = useQueryClient();
  const [q, setQ] = useState('');
  /** v0.43：左侧文件夹树选中的**归属范围**（全部 / 某个文件夹 / 未分类）。
   *
   *  ★ 用一个判别联合而不是 `folderId?: number | null`：分类树上「未分类」**也是**
   *    一个可点的节点，用散参数时 `null` 会同时表示"不筛选"和"未分类"两种意思
   *    —— 那正是 `folder_id=0` 那类兜底 bug 的温床。
   */
  const [scope, setScope] = useState<PartMasterScope>({ kind: 'all' });
  const [sel, setSel] = useState<Set<number>>(new Set());
  /** v0.86：正在就地编辑的「零件编号」—— `mc` = 物料编码（唯一键）、`value` = 输入值。
   *  ★ 用**物料编码**而不是行 id 定位：后端覆盖表的唯一键就是 `material_code`，
   *    用 id 还得再换一次，多一个能对错的机会。 */
  const [pnEdit, setPnEdit] = useState<{ mc: string; value: string } | null>(null);
  /** 防重复保存：Enter 与 blur 会**同时**触发（Enter 先卸载 input，blur 紧跟着来），
   *  没有这道闸就会保存两次、弹两条 toast。 */
  const pnBusyRef = useRef(false);
  /** v0.64：批量导出对话框（按零件打包图纸 + 作业指导书 + 测量报告）。
   *  ⚠️ 与批量条上原有的「导出所选」**不是一回事**：那个只导台账 CSV 一行数据，
   *     这个打的是整套检验资料。两者并列摆着，按钮文案要能区分开。 */
  const [batchOpen, setBatchOpen] = useState(false);
  const [openId, setOpenId] = useState<number | 'new' | null>(null);
  /** v0.41（BOM）：哪些行**展开了子零件**。刻意用「行内展开、不复制行」——
   *  共用件会同时出现在多个总成下，真按树复制行的话「一行 = 一个台账行」就不成立，
   *  勾选 / 批量删除 / 导出会全部错乱（这三件事都按 `id` 走）。 */
  const [expandedBom, setExpandedBom] = useState<Set<number>>(new Set());
  /** v0.42：单击/双击去抖用的挂起定时器（见 `rowClick` 的注释）。
   *  ⚠️ 必须放 ref：它不参与渲染；用 state 只会多一次重渲染，且 `setState` 是异步的，
   *     第二次点击读到的会是旧值。 */
  const rowClickRef = useRef<{ id: number; timer: number } | null>(null);
  /** v0.44：被用户勾掉的列（持久化到本机）。
   *  v0.45：**默认只关 8 项**（= 列表上摆 `DEFAULT_COLS` 那 8 项 + 图纸列）。
   *  存"隐藏的"而非"显示的"——理由见 `HIDDEN_COLS_LS_KEY` 的注释。 */
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => loadHiddenCols());
  /** v0.44：「列」弹层是否展开。 */
  const [colsOpen, setColsOpen] = useState(false);

  /** v0.48：是否连子零件一起列出来。
   *
   *  默认 **false** = 只列顶层零件 —— 子零件不单独占一行，入口是主零件（总成）
   *  的展开区。用户 2026-10-13 原话：「子零件能不能在主零件下显示…
   *  就不要单独占用行了」。
   *  ★ 折叠 ≠ 消失，必须有这个开关兜底（本项目纪律：撤掉一个能力，
   *    必须留一个能找回来的入口）：共用件、"我就想按零件号直接找到它"、
   *    以及子零件归在别的文件夹里看不出来的场景，都靠它。
   */
  const [showAllParts, setShowAllParts] = useState(false);
  /** v0.48：子零件被折叠后不在 `rows` 里 —— 从展开区双击它时按 id 现拉的那一行。
   *
   *  ⚠️ 光靠 `rows.find` 不行：那会让「点展开区里的子件名字」变成
   *     「这个零件不在当前筛选结果里」的报错 —— 而这恰恰是新版最常做的操作。
   */
  const [fetchedRow, setFetchedRow] = useState<PartMasterRow | null>(null);

  const listQ = useQuery({
    // ★ v0.43：`scope` 必须进 queryKey —— 漏了它，切换文件夹时 React Query 会
    //   认为"同一个查询"而**直接返回上一份缓存**，界面表现为「点了文件夹没反应」。
    // ★ v0.48：`showAllParts` 同理 —— 换了口径就是另一份数据，不进 key 会切了没反应。
    queryKey: ['part-master', q, scope, showAllParts],
    queryFn: () => listPartMasterPage(q || undefined, scope, !showAllParts),
  });
  const rows = useMemo(() => listQ.data?.rows ?? [], [listQ.data]);
  /** v0.48：当前筛选条件下**被折叠**的子零件条数（提示条报数用）。
   *  与列表同一套筛选条件（后端 `_part_master_list_q` 共用）⇒ 数字和眼前这一屏对得上。 */
  const hiddenChildren = listQ.data?.hiddenChildren ?? 0;

  /** v0.44：这一屏**实际显示**哪些列（全集减去用户勾掉的）。
   *  v0.45：默认 = `DEFAULT_COLS` 那 8 项 + 元信息列（图纸）。
   *  ⚠️ 只影响**列表视图**：导出 与 16 项编辑面板始终是全量 ——
   *     那是数据本身，不跟着视图走。 */
  const visibleCols = useMemo(
    () => COLS.filter((c) => !hiddenCols.has(String(c.key))), [hiddenCols]);

  /** 勾上 / 勾掉一列。
   *
   *  ⚠️ 判"至少保留一列"必须放在 `setState` **外面**：React 18 StrictMode 会把
   *     状态更新函数调用两次，提示写在里面就会弹两次 toast。
   */
  const toggleCol = useCallback((k: string) => {
    const nxt = new Set(hiddenCols);
    if (nxt.has(k)) {
      nxt.delete(k);
    } else {
      if (COLS.length - nxt.size <= 1) { onToast('至少保留一列', 'error'); return; }
      nxt.add(k);
    }
    saveHiddenCols(nxt);
    setHiddenCols(nxt);
  }, [hiddenCols, onToast]);

  /** v0.45：回到默认的 9 列（8 项内容 + 图纸）。
   *
   *  ★ 这里**必须重建一个 Set**，不能把 `DEFAULT_HIDDEN` 直接塞进 state：
   *    它是模块级常量、被所有实例共享，一旦被后续 `toggleCol` 就地改动，
   *    默认值就永久变了 —— 而且只在**另一个**实例上看得出来。
   */
  const resetCols = useCallback(() => {
    const nxt = new Set(DEFAULT_HIDDEN);
    saveHiddenCols(nxt);
    setHiddenCols(nxt);
  }, []);

  /**
   * 台账侧「数据变了」的**唯一刷新入口**。
   *
   * v0.56：从"只刷 `['part-master']`"扩成 `invalidateDrawingGraph` ——
   * 用户在零件主数据里做**任何**改动（改 16 项字段 / 归类文件夹 / 关联图纸 /
   * 增删零件）都可能影响下游：图纸区的零件计数、SIP 的**只读表头**
   * （客户/材质/工艺流程就是从台账读的）、SIP 的「本零件名下图纸」。
   * 只刷台账那一个键，切过去就是 30 秒内的旧缓存 ⇒ 用户的原话正是
   * 「改了没同步」「三者需要联动」。
   */
  const refresh = useCallback(() => { invalidateDrawingGraph(qc); }, [qc]);

  /** v0.86（沈哥「允许用户维护零件编号」）：保存客户物料编码。
   *
   *  写的是**本地覆盖层** `part_no_overrides`（不是 BOM 镜像表）——
   *  镜像表每天 03:10 被 TRUNCATE 重建，写那里等于白写。
   *  空值 ⇒ 删掉覆盖、回落到 BOM 镜像值（通常就是空）。 */
  const savePartNo = useCallback(async (mc: string, val: string) => {
    if (pnBusyRef.current) return;          // Enter 与 blur 会连着来，只处理一次
    pnBusyRef.current = true;
    setPnEdit(null);
    const v = (val || '').trim();
    try {
      await setPartNoOverride(mc, v);
      onToast(v ? `零件编号已更新：${v}` : '已清除自定义零件编号（回落到 BOM 值）',
              'success');
      await qc.invalidateQueries({ queryKey: ['part-master'] });
    } catch (e: any) {
      onToast(`保存失败：${e.message}`, 'error');
    } finally {
      // 稍后再放开：让 Enter 那次触发的 blur 落空
      setTimeout(() => { pnBusyRef.current = false; }, 0);
    }
  }, [onToast, qc]);

  /**
   * v0.43：文件夹树 —— **与图纸区共用同一份缓存**（queryKey `['folders']`，
   * 与 `DrawingTree.tsx` 逐字一致）。
   *
   * ★ 这是「复用同一棵树」的直接收益：在图纸区改个夹子名，零件区的树立刻是新的。
   *   两处各用各的 key 就会出现"一边改了、另一边还显示旧名字"。
   */
  const treeQ = useQuery({ queryKey: ['folders'], queryFn: getFolderTree });
  /** v0.85：左栏的**客户维度**统计（只含 P/B/D 开头的物料，与列表同一口径）。
   *  ⚠️ 不要在前端重排 —— 后端已把「其他」（`__none__`）排在最后。 */
  const customerQ = useQuery({
    queryKey: ['part-customers'],
    queryFn: fetchPartCustomers,
    refetchOnWindowFocus: false,
  });
  /** 归类 / 移动 / 文件夹增删改之后：与 `refresh` 同一件事（同一张依赖图）。 */
  const refreshAfterMove = refresh;

  /** 树 → 扁平列表（带缩进层级），给行内 / 批量的文件夹下拉用。
   *  ★ **一次算好再用**：在 `<option>` 里递归的话，每渲染一个选项就要走一遍树。 */
  const flatFolders = useMemo(
    () => flattenFolders(treeQ.data?.folders ?? []), [treeQ.data]);
  const nameById = useCallback(
    (id: number) => flatFolders.find((f) => f.id === id)?.name ?? `#${id}`,
    [flatFolders]);

  // v0.40：底部的「未归户图纸」那一行要**报数**（只写「未归户图纸」不给张数，
  // 用户没法判断值不值得点进去）。数从图纸全集里现算，不新增接口。
  const drawQ = useQuery({ queryKey: ['drawings'], queryFn: listDrawings });
  const unassignedN = useMemo(
    () => (drawQ.data ?? []).filter((d) => d.part_master_id == null).length,
    [drawQ.data]);

  // 从图纸工作台跳进来时自动展开目标行（跳转只做一次，随后把焦点交还 App）
  useEffect(() => {
    if (focusId != null) {
      setOpenId(focusId);
      onFocusConsumed?.();
    }
  }, [focusId, onFocusConsumed]);

  // v0.56：从**图纸区**点「M 个零件」跳进来时，把左侧范围切到那个文件夹。
  // ⚠️ 走的必须是与"手点文件夹"**同一条**路径（`setScope`）—— 不另写一套
  //    "按文件夹取数"的实现，否则列表口径迟早与文件夹树那一侧分叉。
  useEffect(() => {
    if (focusFolderId != null) {
      setScope({ kind: 'folder', id: focusFolderId });
      onFocusFolderConsumed?.();
    }
  }, [focusFolderId, onFocusFolderConsumed]);

  // 列表变化后清理幽灵选中 id（否则删完一轮，计数还挂着已消失的行）
  useEffect(() => {
    const alive = new Set(rows.map((r) => r.id));
    setSel((prev) => {
      const next = new Set([...prev].filter((id) => alive.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const allChecked = rows.length > 0 && sel.size === rows.length;
  const someChecked = sel.size > 0 && !allChecked;
  const headRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headRef.current) headRef.current.indeterminate = someChecked;
  }, [someChecked]);

  const toggle = (id: number) => setSel((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

  const selRows = rows.filter((r) => sel.has(r.id));
  /** 编辑面板要打开的那一行。
   *  v0.48：`rows` 里找不到时再看 `fetchedRow` —— 子零件默认不在列表里，
   *  它是从展开区点进来的（见 `jumpToPart`）。
   *  ⚠️ `null` 在 `EditPanel` 里代表**新增模式** ⇒ 这里绝不能把
   *     "还没拉回来" 当成 `null`，那会让用户以为打开了一个空白新零件。 */
  const openRow = openId === 'new' ? null
    : rows.find((r) => r.id === openId)
      ?? (fetchedRow && fetchedRow.id === openId ? fetchedRow : null);

  /** 展开 / 收起某一行的子零件。 */
  const toggleBom = (id: number) => setExpandedBom((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  /** v0.42（用户 2026-10-11 口径）：**单击行 = 展开子零件，双击行 = 打开 16 项编辑**。
   *
   * ⚠️ 双击在浏览器里**先发两次 `click`、再发一次 `dblclick`** ⇒ 若把展开直接挂在
   *    `onClick`、编辑挂在 `onDoubleClick`，双击时会先展开（或收起）一次、再弹面板，
   *    用户看到的是「面板开了，可子零件状态被顺手改了」——最难查的那类"顺手改"。
   *    正解 = **单击动作延迟 `ROW_CLICK_DELAY_MS` 后执行**；这期间若来了同一行的
   *    第二次点击就**取消**它。
   *    取 220ms：Windows 默认双击间隔 500ms，但工程上延迟越短越不显迟滞；只有在
   *    **同一行**上连点才会被吸收（换行点 = 立即执行上一行的单击，不串台）。
   * ⚠️ 不用 `onDoubleClick`：那样仍要拦掉前两次 `click`，等于同一件事写两遍。
   * ⚠️ 行内其它可点区域（勾选框 / BOM 列 / 图纸列）都已 `stopPropagation`，
   *    不会走到这里。
   */
  const rowClick = (id: number) => {
    const pending = rowClickRef.current;
    if (pending && pending.id === id) {          // 第二次点击 ⇒ 编辑 16 项
      window.clearTimeout(pending.timer);
      rowClickRef.current = null;
      setOpenId(id);
      return;
    }
    if (pending) {                               // 点了别的行 ⇒ 先结掉上一行
      window.clearTimeout(pending.timer);
      rowClickRef.current = null;
    }
    const timer = window.setTimeout(() => {
      rowClickRef.current = null;
      toggleBom(id);                             // 单击 ⇒ 展开 / 收起子零件
    }, ROW_CLICK_DELAY_MS);
    rowClickRef.current = { id, timer };
  };

  // 卸载时清掉挂起的定时器 —— 否则切走栏目后它仍会触发一次 state 更新
  useEffect(() => () => {
    if (rowClickRef.current) window.clearTimeout(rowClickRef.current.timer);
  }, []);

  /**
   * 从展开的子件跳到那个零件的编辑面板。
   *
   * ⚠️ 目标必须**先在当前列表里**：`openRow` 是从 `rows` 里 find 出来的，
   *    找不到时是 `null`，而 `null` 在 `EditPanel` 里代表**新增模式** ——
   *    直接 `setOpenId(id)` 会在搜索结果里"打开一个空白的新增零件"，
   *    看起来像数据丢了。所以这里先判存在性，不在就明说。
   *
   * ★ v0.48 改了这条规则的另一半：子零件**默认不在 `rows` 里**（它被折叠在
   *   主零件下面），这时"不在列表里"**不是异常**，而是常态 —— 也就是说
   *   旧写法会把「点展开区里的子件名字」这个最常见的操作变成一条报错。
   *   正解 = 按 id 现拉一行再打开（`getPartMaster`），拉不到才算真出错。
   */
  const jumpToPart = useCallback(async (id: number) => {
    if (rows.some((r) => r.id === id)) { setOpenId(id); return; }
    try {
      const row = await getPartMaster(id);
      setFetchedRow(row);
      setOpenId(id);
    } catch (e: any) {
      onToast(`打不开这个零件：${e.message}`, 'error');
    }
  }, [rows, onToast]);

  /** v0.48：关掉编辑面板时把现拉的那一行也清掉 ——
   *  否则它会在 state 里留着，下次打开另一个 id 时被误命中。 */
  const closePanel = useCallback(() => {
    setOpenId(null);
    setFetchedRow(null);
  }, []);

  /** v0.45：改某一行的文件夹归属。
   *
   *  ★ 抽成**一处**：这段逻辑原来内联在「文件夹」列那格的 `onChange` 里，
   *    本版列表撤了那一列、入口搬到展开行 —— 若照抄一份就是
   *    「同一语义两份实现」（本项目「改了没反应」的头号诊断）。
   */
  const moveRowTo = useCallback(async (r: PartMasterRow, fid: number | null) => {
    try {
      const res = await setPartFolder(r.id, fid);
      // v0.57：图纸跟随零件 —— 一次「移到某夹子」会连带挪动**图纸工作台**那一屏
      // 里的对象。张数取返回行的 `drawing_count`（该零件名下全部图纸都跟着走），
      // 不许写死；0 张时不加这句，否则会凭空多出一个「0 张图纸」的噪音。
      const n = res?.drawing_count ?? 0;
      const extra = n > 0 ? `（含名下 ${n} 张图纸）` : '';
      onToast((fid == null
        ? `已把「${partLabel(r)}」移到未分类`
        : `已把「${partLabel(r)}」移到「${nameById(fid)}」`) + extra,
        'success');
      refreshAfterMove();
    } catch (e: any) {
      onToast(`移动失败：${e.message}`, 'error');
    }
  }, [onToast, nameById, refreshAfterMove]);

  return (
    <div className="pm-wrap">
      {/* ---------- 工具条 ---------- */}
      <div className="pm-toolbar">
        <div className="pm-title">
          零件主数据
          <span className="pm-count">{rows.length} 条</span>
          {/* v0.48：条数变少时**必须当场给解释** ——
              否则用户第一反应是「我的零件丢了」。
              这里报的是与当前筛选（关键字 / 文件夹 / 未分类）**同一套条件**算出来的数，
              点一下就能把子零件全平铺出来。 */}
          {hiddenChildren > 0 && (
            <button className="pm-folded"
                    title={`这个范围里还有 ${hiddenChildren} 个子零件折叠在主零件下面 ——`
                           + `点主零件编号左边的「+」能看到它们，或点这里全部平铺出来`}
                    onClick={() => setShowAllParts(true)}>
              ＋{hiddenChildren} 个子零件已折叠
            </button>
          )}
        </div>
        {/* 说清楚「为什么不能新增」—— 否则用户第一反应是「功能坏了/我权限不够」 */}
        <span style={{ fontSize: 12, color: '#5f5e5a', whiteSpace: 'nowrap',
                       border: '1px solid #d3d1c7', borderRadius: 10,
                       padding: '2px 8px' }}
              title="零件与物料由 BOM 平台维护，本系统只做只读镜像（每天 03:10 同步）。
                     要新增/修改物料，请去 BOM 平台。">
          物料来源：BOM · 只读
        </span>
        <input className="pm-search" value={q} placeholder="搜索 图号 / 名称 / 客户 / 模具号 / 材质…"
               onChange={(e) => setQ(e.target.value)} />
        {/* 🔴 v0.83：这里原本是「新增零件」按钮 —— **整块删掉，不是靠 canEdit 藏**。
            理由：`setOpenId('new')` 会打开 16 字段的录入弹窗，而后端
            `POST /api/part-master` 已经 410 Gone。留着条件渲染的话，
            哪天谁把 canEdit 改回 true（比如给某个角色单独放开文件夹操作时），
            这个死入口就复活了 —— 而它复活后**没有任何测试会失败**。
            零件与物料的唯一来源是 BOM（`bom_part_ref` 镜像，每天同步）。 */}
        <button className="btn" onClick={async () => {
          try {
            const fn = await exportPartMaster(sel.size ? [...sel] : undefined);
            onToast(`已导出：${fn}`, 'success');
          } catch (e: any) { onToast(`导出失败：${e.message}`, 'error'); }
        }}>{sel.size ? `导出所选 ${sel.size} 条` : '导出全部'}</button>
        {/* v0.44：给一个「我要看哪几列」的开关。
            v0.45：列表默认只摆用户点名的 9 列 ⇒ 这个开关同时是
            「把撤下去的 8 项加回来」的入口。
            显示的是「已显示 / 全部」，让用户一眼知道还有几列没摆出来。 */}
        <button className="btn" onClick={() => setColsOpen(true)}
                title="选择列表上显示哪几项（共 16 项可选；只影响列表，不影响导出与编辑面板）。默认只开常用的 8 项，加上固定的「图纸」列，列表上就是 9 列。">
          列 {visibleCols.length}/{COLS.length}
        </button>
        {/* v0.48：折叠的**兜底入口**。
            ★ 撤掉一个能力必须留一条能找回来的路 —— 子零件默认不占行，
              但"共用件 / 我只想按零件号直接找到它 / 它归在别的文件夹里"
              这几种情况都要能看见全部零件，否则就是"用户点不到"。 */}
        <label className="pm-showall"
               title={showAllParts
                 ? '现在列出全部零件（含子零件）—— 子零件会跟主零件一样各占一行。取消勾选回到「只列顶层零件」。'
                 : '默认只列顶层零件，子零件在主零件（总成）编号左边的「+」里展开可见。勾上则把子零件也平铺进列表。'}>
          <input type="checkbox" checked={showAllParts}
                 onChange={(e) => setShowAllParts(e.target.checked)} />
          <span>显示子零件</span>
        </label>
      </div>

      {/* ---------- v0.44：列显示开关 ---------- */}
      {colsOpen && (
        <div className="pm-modal-mask" onClick={() => setColsOpen(false)}>
          <div className="pm-modal pm-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h4>列表显示哪几列</h4>
            <p className="pm-cols-note">
              主数据的 <strong>16 项内容</strong>都在这里，<strong>默认只开常用的 8 项</strong>；
              列表上另外还有一列固定的「<strong>图纸</strong>」（行级信息，不在这 16 项里、
              也不能关）—— 所以列表正常是 <strong>9 列</strong>。
              勾上/勾掉只影响<strong>列表显示</strong>，导出与双击打开的 16 项编辑面板始终是完整的。
              这个设置记在本机上，换台电脑不受影响。
            </p>
            <div className="pm-cols-box">
              {PM_FIELD_GROUPS.map(([g, keys]) => (
                <div key={g} className="pm-cols-group">
                  <div className="pm-cols-gname">{g}</div>
                  <div className="pm-cols-items">
                    {keys.map((k) => (
                      <label key={String(k)} className="pm-cols-item">
                        <input type="checkbox"
                               checked={!hiddenCols.has(String(k))}
                               onChange={() => toggleCol(String(k))} />
                        <span>{PM_FIELD_CN[String(k)] ?? String(k)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="pm-modal-foot">
              {/* v0.45：「恢复全显示」改成两个动作 ——
                  用户本轮的默认就是 9 列，所以"回默认"和"全摊开"是两件不同的事，
                  合成一个按钮会让"恢复默认"变回 16 列（与本版口径相反）。 */}
              <button className="btn" onClick={resetCols}
                      title="回到默认的 9 列（零件编号 / 零件名称 / 客户 / 项目号 / 内部编号 / 模具编号 / 材料牌号 / 材料规格 + 图纸）">
                恢复默认
              </button>
              <button className="btn" onClick={() => {
                const empty = new Set<string>();
                saveHiddenCols(empty);
                setHiddenCols(empty);
              }}>显示全部 16 列</button>
              <button className="btn primary" onClick={() => setColsOpen(false)}>完成</button>
            </div>
          </div>
        </div>
      )}

      <div className="pm-body">
        {/* ---------- 左侧：文件夹分类树（v0.43） ---------- */}
        {/* ★ v0.85（沈哥 2026-10-22）：「左侧的客户用的是文件夹的方式做维度，
            物料表有客户字段，改成用客户字段作为维度筛选」。
            ⇒ 左栏从**文件夹树**换成**客户清单**（文件夹树仍用于图纸侧 `DrawingTree`，
              本页不再渲染它）。客户为空的那些由后端归到 `__none__`，界面叫「其他」。 */}
        <CustomerSidePanel scope={scope} onScope={setScope}
                           data={customerQ.data} loading={customerQ.isLoading} />

        <div className="pm-main">
          {/* ---------- 批量条（选中 ≥1 行才出现，§⑥-3） ---------- */}
          {sel.size > 0 && (
            <div className="pm-bulkbar">
              <span className="pm-bulk-count">已选 {sel.size} 条</span>
            <button className="btn sm" onClick={async () => {
              try {
                const fn = await exportPartMaster([...sel]);
                onToast(`已导出 ${sel.size} 条：${fn}`, 'success');
              } catch (e: any) { onToast(`导出失败：${e.message}`, 'error'); }
            }}>导出所选</button>
            {/* v0.64：整套打包。与上面那个「导出所选」（只导台账 CSV）区分开 ——
                按钮上直接写「整套」二字，不必让用户点开才知道差别。 */}
            <button className="btn sm primary" onClick={() => setBatchOpen(true)}
                    title="按零件打包：零件主数据 + 气泡图 + 作业指导书各工序 + 测量报告，可选内容">
              批量导出（整套）
            </button>
            {/* v0.43：批量归类。用原生 `<select>` 而不是自绘弹层 ——
                自绘弹层要处理 capture 抢跑 / 掩膜 / 视口边界，而这里只是个选择器。 */}
            <select className="pm-move-sel" value="" disabled={flatFolders.length === 0}
                    title={flatFolders.length === 0
                      ? '还没有任何文件夹 —— 先在上面「+ 新建文件夹」建一个'
                      : `把选中的 ${sel.size} 个零件一起移过去`}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (!v) return;
                      const fid = v === '__none__' ? null : Number(v);
                      try {
                        const res = await bulkSetPartFolder([...sel], fid);
                        // v0.57：这批零件的图纸跟着一起搬（`moved_drawings`
                        // 是后端实际挪动的张数）—— 图纸在**另一个页签**上，
                        // 不报这个数，用户会以为它们还留在原地。
                        const extra = res.moved_drawings
                          ? `（含名下 ${res.moved_drawings} 张图纸）` : '';
                        onToast((fid === null
                          ? `已把 ${res.moved} 个零件移到未分类`
                          : `已把 ${res.moved} 个零件移到「${res.folder_name}」`) + extra,
                          'success');
                        setSel(new Set());
                        refreshAfterMove();
                      } catch (err: any) {
                        onToast(`批量移动失败：${err.message}`, 'error');
                      }
                    }}>
              <option value="">移动到文件夹…</option>
              <option value="__none__">（未分类）</option>
              {flatFolders.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
            <BulkDelete selRows={selRows} onDone={() => { setSel(new Set()); refresh(); }}
                        onToast={onToast} />
              <button className="btn sm" onClick={() => setSel(new Set())}>取消选择</button>
            </div>
          )}

          {/* ---------- 提示条：**在表格上方**（§⑥-6） ---------- */}
          <div className="pm-hintbar">
            <strong>9 项内容</strong>固定摆在表里（工具条上的「<strong>列</strong>」能把另外 8 项加回来；
            这个设置只影响列表，导出与编辑面板始终是完整 16 项）。
            「零件编号」左边的 <strong>+ / −</strong>、或<strong>单击</strong>一行，
            打开它的<strong>子零件</strong>；<strong>双击</strong>一行编辑这 16 项。
            <strong>左侧文件夹</strong>点一下就是筛选；展开行里的<strong>文件夹下拉</strong>直接改这一行的归属
            （框选多行可以用批量条里的「移动到文件夹」）。手填过的字段会被锁定，重新识别不会覆盖。
          </div>

          {/* ---------- 列表 ---------- */}
          <div className="pm-tablebox">
            <table className="pm-table">
              <thead>
                <tr>
                  <th className="pm-checkcol">
                    {canEdit && (
                      <input ref={headRef} type="checkbox" checked={allChecked}
                             onChange={toggleAll} aria-label="全选" />
                    )}
                  </th>
                  {visibleCols.map((c) => <th key={String(c.key)}>{c.label}</th>)}
              {/* v0.45：这里原来的「文件夹」列与「BOM」列，按用户 2026-10-11 的清单
                  从列表撤掉了 —— 列表只留「零件编号…材料规格」+「图纸」这 9 项。
                  · BOM 列的能力（展开子零件 / 共用件提示）搬到「零件编号」左边的 +/− 上；
                  · 文件夹归属的下拉搬到**展开行**里（点 +/− 就能看到并改）。
                  撤下去不等于删掉：两样都还在，只是不再各占一列。 */}
                  <th className="pm-dwgcol" title="该零件已归户的图纸张数（点它钻取到图纸区）；右边「SIP」= 编辑这些图纸的作业指导书。0 张 = 没有任何图纸指向这一行（台账里的“孤行”），不是错误，但值得留意。">
                    图纸
                  </th>
                  <th className="pm-arrowcol" />
                </tr>
              </thead>
              <tbody>
            {listQ.isLoading && <tr><td colSpan={visibleCols.length + 3} className="pm-empty">加载中…</td></tr>}
            {!listQ.isLoading && rows.length === 0 && (
              <tr><td colSpan={visibleCols.length + 3} className="pm-empty">
                {scope.kind === 'all' ? (
                  <>还没有零件记录。零件与物料由 <b>BOM 平台</b>维护 ——
                    每天 03:10 自动同步（也可手动跑 <code>scripts/sync_bom.py</code>），
                    同步后这里会自动出现。本页只读，不再提供手工新增。</>
                ) : (
                  /* ⚠️ 范围筛选下的一条都查不到，**不能说成「还没有零件记录」** ——
                     那会让用户以为数据没了。必须指明是"这个范围里没有"，
                     并给一条回到全部的路。 */
                  <>
                    这个范围内没有零件
                    {scope.kind === 'unfiled' ? '（未分类是空的）' : ''}。
                    换一个文件夹，或
                    <button className="pm-linkbtn" onClick={() => setScope({ kind: 'all' })}>
                      看全部零件
                    </button>。
                  </>
                )}
              </td></tr>
            )}
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr className="pm-row" onClick={() => rowClick(r.id)}>
                      <td className="pm-checkcol" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)}
                                 aria-label={`选择 ${r.part_no ?? r.id}`} />
                        )}
                      </td>
                      {visibleCols.map((c) => (
                        /* v0.45：「零件编号」这一格额外带一个展开子零件的 **+ / −**。
                           用户原话：「零件编号旁边增加个 + 或者箭头，打开有子零件的信息」。
                           它原来是一个独立的「BOM」列 —— 撤列之后能力搬到编号旁边，没有丢。
                           ⚠️ 为什么用按钮、而不是让整格可点：这一行的**单击=展开、双击=编辑**
                              都走 `rowClick` 那套去抖；整格可点的话，用户**想双击编辑**时
                              第一下会先被算成"单击展开"。按钮里 `stopPropagation` 把这条
                              链路掐干净，两个动作互不干扰。
                           ⚠️ `parent_count > 1` 的「共用 ×N」原来也在 BOM 列，一并搬过来 ——
                              那是"同一个零件装了好几处"的唯一提示，撤列时不能顺手丢掉。 */
                        c.key === 'part_no' ? (
                          <td key={String(c.key)} className="pm-partno-cell">
                            <button className={`pm-exp${expandedBom.has(r.id) ? ' on' : ''}`}
                                    aria-expanded={expandedBom.has(r.id)}
                                    onClick={(e) => { e.stopPropagation(); toggleBom(r.id); }}
                                    title={r.child_count > 0
                                      ? `展开它下面的 ${r.child_count} 个子零件`
                                      : '展开看看它下面有哪些子零件（现在还没有 —— 展开区里可以加）'}>
                              {expandedBom.has(r.id) ? '−' : '+'}
                            </button>
                            {pnEdit?.mc === (r.internal_no ?? '') ? (
                              /* v0.86：就地编辑零件编号。Enter 保存 / Esc 取消 / 失焦保存。 */
                              <input className="pm-input" autoFocus
                                     style={{ width: 132, fontSize: 12, padding: '1px 4px' }}
                                     value={pnEdit.value}
                                     placeholder="客户物料编码"
                                     onClick={(e) => e.stopPropagation()}
                                     onChange={(e) => setPnEdit({
                                       mc: r.internal_no ?? '', value: e.target.value })}
                                     onKeyDown={(e) => {
                                       if (e.key === 'Enter') {
                                         e.preventDefault();
                                         void savePartNo(r.internal_no ?? '', pnEdit.value);
                                       } else if (e.key === 'Escape') {
                                         e.preventDefault();
                                         setPnEdit(null);
                                       }
                                     }}
                                     onBlur={() => void savePartNo(r.internal_no ?? '',
                                                                   pnEdit.value)} />
                            ) : (
                              <>
                                <span className="pm-partno"
                                      title={`${r.part_no || '（空）'}`
                                        + ((r as any).part_no_override
                                          ? ` · 本地维护（${(r as any).part_no_source === 'kingdee' ? '金蝶回填' : '人工填写'}）`
                                          : ' · 来自 BOM 镜像')}>
                                  {r.part_no || <span className="pm-nil">—</span>}
                                  {(r as any).part_no_override && (
                                    <em style={{ fontSize: 10, color: '#854f0b',
                                                 marginLeft: 3, fontStyle: 'normal' }}>•</em>
                                  )}
                                </span>
                                <button className="btn sm"
                                        style={{ padding: '0 5px', fontSize: 11,
                                                 marginLeft: 4, lineHeight: 1.4 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPnEdit({ mc: r.internal_no ?? '',
                                                      value: r.part_no ?? '' });
                                        }}
                                        title="维护零件编号（客户物料编码）—— 存本地覆盖层，不会被 BOM 每日同步冲掉">
                                  ✎
                                </button>
                              </>
                            )}
                            {r.parent_count > 1 && (
                              <em className="pm-shared"
                                  title={`共用件：同一个零件装在 ${r.parent_count} 个总成下面（用量 / 位号各自独立）`}>
                                共用 ×{r.parent_count}
                              </em>
                            )}
                          </td>
                        ) : (
                          <td key={String(c.key)} title={(r as any)[c.key] ?? ''}
                              className={(r as any)[c.key] ? undefined : 'pm-nil-cell'}>
                            {(r as any)[c.key] || <span className="pm-nil">—</span>}
                          </td>
                        )
                      ))}
                    {/* v0.45：原来这里的「文件夹」列 +「BOM」列已撤（理由见 thead 里那条注释）。
                        · 改归属 → 展开行里的文件夹下拉（`moveRowTo`）；
                        · 展开子零件 / 共用件 → 「零件编号」左边的 +/− 与「共用 ×N」小标。
                        ⚠️ 撤列的**边界**要守住：撤的只是"列表上多一列"，
                           能力一个都不能少 —— 少了就是"用户点不到"，比多一列严重得多。 */}
                      {/* v0.51：包一层 inline-flex（`.pm-dwg-wrap`），让「张数签 + SIP 签」
                          成为同一排两张**等高小签** —— 此前张数是点线链接、SIP 是 10px
                          小药丸，两种视觉语言挤在一格里（用户 2026-10-15 截图反馈）。 */}
                      <td className="pm-dwgcol">
                        <span className="pm-dwg-wrap">
                        {r.drawing_count > 0
                          ? <button className="pm-dwg-n"
                                    title={`查看这个零件名下的 ${r.drawing_count} 张图纸`}
                                    onClick={(e) => {
                                      // 别触发行点击（那是打开编辑面板）—— 这里走「钻取到图纸区」。
                                      e.stopPropagation();
                                      onOpenDrawings({
                                        kind: 'part', id: r.id,
                                        label: [r.part_no, r.part_name].filter(Boolean).join(' · ')
                                               || `零件 #${r.id}`,
                                      });
                                    }}>
                              {r.drawing_count} 张 ▸
                            </button>
                          : <span className="pm-dwg-none"
                                  title="没有任何图纸指向这一行 —— 图纸被删或还没关联。行不会自动删除，需要时就点「关联图纸」。">
                              无图纸
                            </span>}
                        {/* v0.50 SIP 入口（用户 2026-10-14「3、两个都要」= 零件行 + 图纸行都放）。
                            ⚠️ 刻意**塞在「图纸」这一格里**、不新增第 10 列：
                               列表默认可见列是**冻结的 9 个字段列**（v0.45 的口径，
                               `scripts`/用例里有顺序护栏）。加一列会把那份护栏推翻，
                               而这里本来就是"这个零件的图纸"那一格，语义上也贴。
                            ⚠️ 0 张时**禁用**：SIP 的检验明细就是图纸的气泡号 + 尺寸，
                               没有图纸就没有可编辑的内容 —— 让人点进一个必然空白的
                               编辑页，比按钮灰着更浪费他的时间。 */}
                        {onOpenSip && (
                          <button className="pm-sip"
                                  disabled={r.drawing_count === 0}
                                  data-sip-part={r.id}
                                  title={r.drawing_count === 0
                                    ? '这个零件名下还没有图纸 —— SIP 的检验明细来自图纸的气泡号与尺寸，先在「关联图纸」里归户一张'
                                    : '编辑这个零件名下的图纸的作业指导书 SIP（原材料 / 制程 / 成品 / 出货 / 自检）'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenSip({
                                      kind: 'part', id: r.id,
                                      label: [r.part_no, r.part_name].filter(Boolean).join(' · ')
                                             || `零件 #${r.id}`,
                                    });
                                  }}>
                            SIP
                          </button>
                        )}
                        </span>
                      </td>
                      <td className="pm-arrowcol"><span className="pm-arrow">›</span></td>
                    </tr>
                    {/* 展开区（v0.48 重构）：子零件**按主表的列**逐行列在主零件下面。
                        ★ 用户 2026-10-13 原话：「子零件能不能在主零件下显示标题的那些
                          信息，就不要单独占用主零件行了」⇒ ① 子零件不再进主列表
                          （`top_only` 过滤掉），② 展开区按「表头勾选了哪几列就显示
                          哪几列」渲染（`cols={visibleCols}`）。
                        ⚠️ 这些行**不是**台账行：不参与勾选 / 批量删除 / 导出
                          （那三件事一律按 `id` 走，而共用件会在多个总成下各出现一次）。
                        ⚠️ 多级 BOM：子件自己还有下级时，会在它下面**继续递归** ——
                          折叠之后，这里是唯一能看全整棵树的地方。 */}
                    {expandedBom.has(r.id) && (
                      <>
                        {/* v0.87：BOM 平台的真子件（只读）。放在老 `BomChildRows`
                            之前 —— 那条走 `bom_edges`（本地手建边表，指向已废弃的
                            `part_master.id`），现在恒空，只作为历史兼容留着。 */}
                        <BomPlatformChildren materialCode={r.internal_no ?? ''}
                                             span={visibleCols.length + 3} />
                        <BomChildRows pmId={r.id} depth={1} cols={visibleCols}
                                      expanded={expandedBom} onToggle={toggleBom}
                                      onJump={jumpToPart} onToast={onToast}
                                      onOpenDrawings={onOpenDrawings} />
                        {/* 文件夹归属：这是**主零件那一行**的归属，仍然就地可改。
                            v0.45：列表撤了「文件夹」列，入口搬到展开区。 */}
                        <tr className="pm-bom-foot">
                          <td colSpan={visibleCols.length + 3}>
                            <div className="pm-exp-fold" onClick={(e) => e.stopPropagation()}>
                              <span className="pm-exp-fold-lb">文件夹</span>
                              {/* v0.47：归户是写操作 ⇒ 只读角色只看归属名，不给下拉。 */}
                              {canEdit ? (
                                <FolderSelect folders={flatFolders} value={r.folder_id}
                                              title="这一行归在哪个文件夹里（图纸与零件共用同一棵树）。空 = 未分类。改完立刻生效。"
                                              onChange={(fid) => moveRowTo(r, fid)} />
                              ) : (
                                <span className="cell-ro fx-ro">
                                  {flatFolders.find((f) => f.id === r.folder_id)?.label ?? '（未分类）'}
                                </span>
                              )}
                              {r.parent_count > 0 && (
                                <em className="pm-shared"
                                    title={r.parent_count > 1
                                      ? `共用件：同一个零件装在 ${r.parent_count} 个总成下面（用量 / 位号各自独立）`
                                      : '装在 1 个总成下面'}>
                                  {r.parent_count > 1
                                    ? `共用件 · 装在 ${r.parent_count} 个总成下`
                                    : '装于总成'}
                                </em>
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>

      {/* v0.40（用户 2026-10-10）：`BOM —— 图纸` 之后，**没有零件的图纸**必须有去处。
          它不属于任何零件，所以放在零件**列表外面**（表格下方独立一条），
          而不是塞成一行假零件 —— 假零件的「图纸数」会跟真的混在一起。 */}
      <div className="pm-unassigned-row" role="button" tabIndex={0}
           onClick={() => onOpenDrawings({ kind: 'unassigned' })}
           onKeyDown={(e) => {
             if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault();
               onOpenDrawings({ kind: 'unassigned' });
             }
           }}
           title="还没归户到任何零件的图纸。归户后会自动出现在对应零件下（只改归属，不动图纸本身）。">
        <span className="pm-unassigned-name">未归户图纸</span>
        <span className="pm-unassigned-n">{unassignedN} 张</span>
        <span className="pm-arrow">›</span>
      </div>

      {openId !== null && (
        /* v0.48：关闭一律走 `closePanel` —— 它顺手清掉 `fetchedRow`，
           否则「从展开区点进来的那一行」会留在 state 里，下次开另一个 id
           时被误命中（现象：打开 A 却显示 B 的内容）。 */
        <EditPanel row={openRow} onClose={closePanel}
                   readOnly={!canEdit}
                   currentDrawingId={currentDrawingId ?? null}
                   onSaved={() => { refresh(); closePanel(); }} onToast={onToast} />
      )}

      {/* v0.64：批量导出（整套）。挂在**列表外层**，因为它是模态框：
          塞进 pm-main 里会被表格的 overflow 裁掉。 */}
      <BatchExportDialog open={batchOpen} partIds={[...sel]}
                         onClose={() => setBatchOpen(false)} onToast={onToast} />
    </div>
  );
}

// ================================================================ 文件夹分类（v0.43）
// 需求（2026-10-11 用户原话）：
//     「主数据增加文件夹分类管理等信息」
//
// ★ 复用图纸区**同一棵树**（`folders` 表 / `GET /api/folders` /
//   React Query 的 `['folders']` 缓存键与 `DrawingTree.tsx` 逐字一致）。
//   本项目头号坑是「同一语义几份实现」—— 再建一棵"零件文件夹"树就等于把
//   「文件夹」这个概念实现两遍（两套重名规则、两套防环、两套删除语义）。
//   ★ 直接收益：在这边改个夹子名，图纸区的树**立刻**也是新的（同一份缓存）。

/** 扁平化的文件夹（带缩进标签），给 `<select>` 用。 */
type FlatFolder = { id: number; name: string; label: string; depth: number };

/** 树 → 扁平列表（前序）。缩进用**全角空格**：`<option>` 里半角空格会被折叠。 */
// ====================================================== v0.85 左栏 · 客户维度
/** 客户维度筛选栏（替代原来的文件夹树）。
 *
 *  沈哥 2026-10-22：「左侧的客户用的是文件夹的方式做维度，物料表有客户字段，
 *  改成用客户字段作为维度筛选……客户字段为空的归入其他」
 *
 *  ★ 三个层次与后端 `GET /part-master/customers` 一一对应：
 *    「全部零件」= `{kind:'all'}` ｜ 某客户 = `{kind:'customer', name}` ｜
 *    「其他」= `{kind:'customer', name:'__none__'}`（客户字段为空）。
 *  ★ 数据现实（实测 2026-10-22）：2443 条 P/B/D 物料里只有 183 条带客户
 *    ⇒ 「其他」2260 条。**这不是 bug** —— BOM 侧的客户字段没填全，
 *    界面照实显示，不粉饰、也不偷偷把空的匀到别的客户名下。
 *  ★ 后端已把「其他」排在最后，这里**不重排**（重排会让它混进真客户中间）。
 */
function CustomerSidePanel({ scope, onScope, data, loading }: {
  scope: PartMasterScope;
  onScope: (s: PartMasterScope) => void;
  data?: { total: number; customers: Array<{ customer: string; label: string; count: number }> };
  loading: boolean;
}) {
  const cur = scope.kind === 'customer' ? scope.name : null;
  const item = (on: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    padding: '5px 8px', marginBottom: 2, border: 'none', borderRadius: 6,
    background: on ? '#e6f1fb' : 'transparent',
    color: on ? '#185fa5' : '#2c2c2a',
    fontWeight: on ? 600 : 400, fontSize: 12.5,
    textAlign: 'left', cursor: 'pointer',
  });
  const rows = data?.customers ?? [];
  return (
    <div className="pm-side">
      <div className="pm-side-head">
        <span className="pm-side-title">客户</span>
      </div>
      <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <button style={item(scope.kind === 'all')}
                onClick={() => onScope({ kind: 'all' })}
                title="不按客户筛（全部 P/B/D 物料）">
          <span style={{ flex: 1 }}>全部零件</span>
          <span style={{ opacity: 0.6 }}>{data?.total ?? '…'}</span>
        </button>
        {loading && <div className="pm-side-note">加载中…</div>}
        {rows.map((c) => {
          const isOther = c.customer === '__none__';
          return (
            <button key={c.customer} style={item(cur === c.customer)}
                    onClick={() => onScope({ kind: 'customer', name: c.customer })}
                    title={isOther
                      ? 'BOM 里客户字段为空的物料（实测占绝大多数 —— 客户字段没填全）'
                      : c.customer}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap',
                             fontStyle: isOther ? 'italic' : 'normal' }}>
                {c.label}
              </span>
              <span style={{ opacity: 0.6 }}>{c.count}</span>
            </button>
          );
        })}
      </div>
      <div className="pm-side-note" style={{ padding: '6px 8px', lineHeight: 1.4 }}>
        物料来源 BOM，只列 <b>P/B/D</b> 开头的编码
      </div>
    </div>
  );
}

function flattenFolders(nodes: FolderNode[], depth = 0,
                        out: FlatFolder[] = []): FlatFolder[] {
  for (const n of nodes) {
    out.push({ id: n.id, name: n.name, label: `${'　'.repeat(depth)}${n.name}`, depth });
    flattenFolders(n.children ?? [], depth + 1, out);
  }
  return out;
}

/** 行内的归属下拉（v0.43）。
 *
 *  ⚠️ 用**原生 `<select>`** 而不是自绘弹层：这一格在表格里，自绘弹层要处理
 *     capture 抢跑 / 掩膜 / 视口边界三件麻烦事，而这里只是个选择器。
 *  ⚠️ **悬空引用**（这一行指向一个已被删掉的文件夹）必须**显式列出来** ——
 *     静默显示成「未分类」的话，用户改完别的行再看这一行会以为自己选错了；
 *     列出来他就能一眼把它改掉。这也是"宁可让用户看见"的落地。
 */
function FolderSelect({ folders, value, onChange, title }: {
  folders: FlatFolder[];
  value: number | null;
  onChange: (id: number | null) => void;
  title?: string;
}) {
  const known = value != null && folders.some((f) => f.id === value);
  const dangling = value != null && !known;
  return (
    <select className={`pm-fold-sel${value == null ? ' unfiled' : ''}`}
            value={value == null ? '' : String(value)}
            disabled={folders.length === 0 && value == null}
            title={folders.length === 0
              ? '还没有任何文件夹 —— 先在左侧点「+ 新建文件夹」'
              : (title ?? '改这一行的归属（图纸与零件共用同一棵树）')}
            onChange={(e) => {
              const raw = e.target.value;
              onChange(raw === '' ? null : Number(raw));
            }}>
      <option value="">（未分类）</option>
      {dangling && (
        <option value={String(value)}>⚠ 已删除的文件夹 #{value}</option>
      )}
      {folders.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
    </select>
  );
}

/** 左侧分类树（v0.43）：全部 / 各文件夹 / 未分类。点一下就是筛选。
 *
 *  ★ 这里**只做筛选与计数**，不做「拖零件到夹子上」—— 归属改动放在
 *    「文件夹」列的下拉与批量条里，两条路径都是显式点击，不引入拖拽这个
 *    在表格 + 滚动容器里极难做对（也极难自检）的交互。
 *  ★ 文件夹的**增 / 改名 / 删除**复用图纸区那三条接口（同一棵树）。
 */
function FolderSidePanel({ scope, onScope, tree, loading, onToast, onTreeChanged,
                           canEdit = true }: {
  scope: PartMasterScope;
  onScope: (s: PartMasterScope) => void;
  tree: FolderTree | null;
  loading: boolean;
  onToast: Toast;
  onTreeChanged: () => void;
  /** v0.47：只读角色没有右键菜单（文件夹的增/改名/删全在里面）。点选筛选照常。 */
  canEdit?: boolean;
}) {
  const [closed, setClosed] = useState<Set<number>>(new Set());   // 折叠态（本地）
  const [menu, setMenu] = useState<{ x: number; y: number; node: FolderNode | null } | null>(null);
  const [prompt, setPrompt] = useState<{ mode: 'new' | 'rename';
                                         node: FolderNode | null } | null>(null);
  const [askDel, setAskDel] = useState<FolderNode | null>(null);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** 关右键菜单。
   *
   *  ★ 必须在**捕获阶段**监听（第 3 个参数 `true`），并判 `menuRef.contains(e.target)`：
   *    只写 `setMenu(null)` 的话，点菜单里那一项时会先被这个监听关掉，
   *    表现为「菜单点不动」（本项目已在图纸区踩过一次）。
   */
  useEffect(() => {
    if (!menu) return;
    const close = (e: Event) => {
      const t = e.target as Node | null;
      if (menuRef.current && t && menuRef.current.contains(t)) return;
      setMenu(null);
    };
    window.addEventListener('mousedown', close, true);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('mousedown', close, true);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const toggleOpen = (id: number) => setClosed((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const total = tree?.folder_count ?? 0;
  const unfiledN = tree?.unfiled_part_count ?? 0;

  const doCreate = async (name: string, parentId: number | null) => {
    setBusy(true);
    try {
      const f = await createFolder(name, parentId);
      if (parentId != null) setClosed((p) => { const n = new Set(p); n.delete(parentId); return n; });
      onToast(`已新建文件夹「${f.name}」`, 'success');
      onTreeChanged();
      setPrompt(null);
    } catch (e: any) { onToast(`新建失败：${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const doRename = async (id: number, name: string) => {
    setBusy(true);
    try {
      const f = await patchFolder(id, { name });
      onToast(`已改名为「${f.name}」`, 'success');
      onTreeChanged();
      setPrompt(null);
    } catch (e: any) { onToast(`改名失败：${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const doDelete = async (node: FolderNode) => {
    setBusy(true);
    try {
      const res = await deleteFolder(node.id);
      // ★ 如实报数：只说「已删除」的话，用户不知道那 5 个零件去哪了。
      onToast(`已删除 ${res.deleted_folders} 个文件夹；` +
              `${res.drawings_to_root} 张图纸退回根目录、` +
              `${res.parts_to_unfiled} 个零件退回未分类（都没有被删）—— ` +
              `整个文件夹可在「回收站」恢复`, 'success');
      onTreeChanged();
      // 当前正看着这个夹子 ⇒ 被迫回到「全部」，否则会停在一个不存在的范围上
      if (scope.kind === 'folder' && scope.id === node.id) onScope({ kind: 'all' });
      setAskDel(null);
    } catch (e: any) { onToast(`删除失败：${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const renderNode = (n: FolderNode, depth: number) => {
    const active = scope.kind === 'folder' && scope.id === n.id;
    const isClosed = closed.has(n.id);
    const hasKids = (n.children?.length ?? 0) > 0;
    return (
      <div key={n.id}>
        <div className={`pm-node${active ? ' on' : ''}`}
             style={{ paddingLeft: 6 + depth * 14 }}
             role="button" tabIndex={0}
             title={`${n.name}：${n.part_count} 个零件（含子文件夹 ${n.total_part_count} 个）；` +
                    `${n.drawing_count} 张图纸（含子孙 ${n.total_drawing_count} 张）。` +
                    `右键可以新建子文件夹 / 改名 / 删除。`}
             onClick={() => onScope({ kind: 'folder', id: n.id })}
             onKeyDown={(e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                 e.preventDefault(); onScope({ kind: 'folder', id: n.id });
               }
             }}
             onContextMenu={(e) => {
               if (!canEdit) return;         // v0.47：只读角色没有右键菜单
               e.preventDefault(); e.stopPropagation();
               setMenu({ x: e.clientX, y: e.clientY, node: n });
             }}>
          {/* 展开三角**独立可点**，点它只折叠不筛选（stopPropagation） */}
          <span className={`pm-tw${hasKids ? '' : ' leaf'}`}
                onClick={(e) => { e.stopPropagation(); if (hasKids) toggleOpen(n.id); }}>
            {hasKids ? (isClosed ? '▸' : '▾') : ''}
          </span>
          <span className="pm-node-name">{n.name}</span>
          {/* ★ 徽标口径必须与**列表**对得上：点一个夹子，列表列的是**仅本夹**的零件
              （`?folder_id=` 是本级，不含子夹）。所以当"含子孙"更大时，写成
              `本夹/含子孙`（如 `2/3`）—— 只写一个 `3` 会变成"树说 3 个、点进去 2 个"。
              这与图纸区 `DrawingTree` 的 `drawing_count/total_drawing_count` **同款**。 */}
          <span className="pm-node-n" title={`零件：本层 ${n.part_count} 个，含子文件夹 ${n.total_part_count} 个`}>
            {n.total_part_count > n.part_count
              ? `${n.part_count}/${n.total_part_count}`
              : n.part_count}
          </span>
        </div>
        {!isClosed && (n.children ?? []).map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <aside className="pm-side">
      <div className="pm-side-head">
        <span className="pm-side-title">文件夹</span>
        <button className="pm-side-add" disabled={busy}
                title="在最外层新建一个文件夹"
                onClick={() => { setMenu(null); setPrompt({ mode: 'new', node: null }); }}>
          + 新建
        </button>
      </div>

      <div className="pm-tree" onContextMenu={(e) => {
        if (!canEdit) return;                // v0.47：只读角色没有右键菜单
        // 空白处右键 = 建顶层文件夹（与图纸区"在下方空白处右键"一致）
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY, node: null });
      }}>
        <div className={`pm-node root${scope.kind === 'all' ? ' on' : ''}`}
             role="button" tabIndex={0}
             onClick={() => onScope({ kind: 'all' })}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault(); onScope({ kind: 'all' }); } }}>
          <span className="pm-tw leaf" />
          <span className="pm-node-name">全部零件</span>
        </div>

        {loading && <div className="pm-side-note">加载中…</div>}
        {!loading && total === 0 && (
          <div className="pm-side-note">还没有文件夹。点上面「+ 新建」或在此处右键新建。</div>
        )}
        {(tree?.folders ?? []).map((n) => renderNode(n, 1))}

        <div className={`pm-node unfiled${scope.kind === 'unfiled' ? ' on' : ''}`}
             role="button" tabIndex={0}
             title="还没有归到任何文件夹里的零件。删掉文件夹时，里面的零件也会回到这里（零件不会被删）。"
             onClick={() => onScope({ kind: 'unfiled' })}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {
               e.preventDefault(); onScope({ kind: 'unfiled' }); } }}>
          <span className="pm-tw leaf" />
          <span className="pm-node-name">未分类</span>
          <span className="pm-node-n">{unfiledN}</span>
        </div>
      </div>

      <div className="pm-side-foot">
        图纸与零件共用这一棵树 —— 在这里改的分类，图纸区看到的是一样的。
      </div>

      {/* ---------- 右键菜单 ---------- */}
      {menu && (
        <div className="ctx-menu" ref={menuRef} style={{ left: menu.x, top: menu.y }}
             onClick={(e) => e.stopPropagation()}
             onContextMenu={(e) => e.preventDefault()}>
          <button className="ctx-item" onClick={() => {
            const target = menu.node; setMenu(null);
            setPrompt({ mode: 'new', node: target });
          }}>{menu.node ? '新建子文件夹' : '新建文件夹'}</button>
          {menu.node && (
            <>
              <button className="ctx-item" onClick={() => {
                const target = menu.node!; setMenu(null);
                setPrompt({ mode: 'rename', node: target });
              }}>重命名</button>
              <button className="ctx-item danger" onClick={() => {
                const target = menu.node!; setMenu(null); setAskDel(target);
              }}>删除文件夹</button>
            </>
          )}
        </div>
      )}

      {/* ---------- 新建 / 改名输入 ---------- */}
      {prompt && (
        <PromptModal
          title={prompt.mode === 'new'
            ? (prompt.node ? `在「${prompt.node.name}」里新建子文件夹` : '新建文件夹')
            : `重命名「${prompt.node?.name}」`}
          initial={prompt.mode === 'rename' ? prompt.node?.name ?? '' : ''}
          busy={busy}
          onCancel={() => setPrompt(null)}
          onSubmit={(v) => {
            const name = v.trim();
            if (!name) { onToast('文件夹名不能为空', 'error'); return; }
            if (prompt.mode === 'new') doCreate(name, prompt.node?.id ?? null);
            else doRename(prompt.node!.id, name);
          }} />
      )}

      {/* ---------- 删除确认（把牌摊开：会牵动多少图纸 / 零件） ---------- */}
      {askDel && (
        <div className="pm-modal-mask" onClick={() => !busy && setAskDel(null)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <h4>删除文件夹「{askDel.name}」？</h4>
            <p>
              会<strong>连子文件夹一起删</strong>。里面的
              <strong> {askDel.total_drawing_count} 张图纸会退回根目录</strong>、
              <strong> {askDel.total_part_count} 个零件会退回未分类</strong> ——
              图纸和零件<strong>都不会被删除</strong>，只是不再归这个类。
            </p>
            <div className="pm-modal-foot">
              <button className="btn" disabled={busy}
                      onClick={() => setAskDel(null)}>取消</button>
              <button className="btn btn-danger" disabled={busy}
                      onClick={() => doDelete(askDel)}>
                {busy ? '删除中…' : '删除文件夹'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/** 极简输入弹层（新建 / 重命名文件夹用）。 */
function PromptModal({ title, initial, busy, onCancel, onSubmit }: {
  title: string;
  initial: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <div className="pm-modal-mask" onClick={() => !busy && onCancel()}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <h4>{title}</h4>
        <input ref={ref} className="pm-input" style={{ width: '100%', marginTop: 8 }}
               value={v} maxLength={120}
               onChange={(e) => setV(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter') onSubmit(v);
                 if (e.key === 'Escape') onCancel();
               }} />
        <p>名字里不能有斜杠 / 或反斜杠 \。</p>
        <div className="pm-modal-foot">
          <button className="btn" disabled={busy} onClick={onCancel}>取消</button>
          <button className="btn primary" disabled={busy} onClick={() => onSubmit(v)}>
            {busy ? '提交中…' : '确定'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================ 编辑面板
function EditPanel({ row, currentDrawingId, onClose, onSaved, onToast,
                     readOnly = false }: {
  row: PartMasterRow | null;
  currentDrawingId: number | null;
  onClose: () => void;
  onSaved: () => void;
  onToast: Toast;
  /** v0.47：只读角色。改不了 16 项、也删不了 —— 面板只作"看详情"用。 */
  readOnly?: boolean;
}) {
  const ro = readOnly;
  const isNew = row === null;
  // v0.61：「客户 / 项目类型 / 模具类型 / 模穴 / 冲床吨位」改由后台清单驱动
  //   （用户 2026-10-17 选的口径 = **严格下拉**）。
  //   ★ 这 5 项历史上是自由文本；`OptionSelect` 会把库里的表外历史值原样补进
  //     下拉并标注「表外值」—— 严格下拉不等于**静默改写老数据**。
  //   ★ 清单为空时（出厂就是空的：模具类型 / 模穴 / 冲床吨位）控件会禁用并
  //     给出「到设置里加」的提示，而不是让人对着空下拉发呆。
  const customerOpts = useOptionValues('customer');
  const projectTypeOpts = useOptionValues('project_type');
  const moldTypeOpts = useOptionValues('mold_type');
  const cavityOpts = useOptionValues('cavity_count');
  const pressOpts = useOptionValues('press_tonnage');
  const optionOptsOf = (k: string): string[] | null => {
    switch (k) {
      case 'customer': return customerOpts;
      case 'project_type': return projectTypeOpts;
      case 'mold_type': return moldTypeOpts;
      case 'cavity_count': return cavityOpts;
      case 'press_tonnage': return pressOpts;
      default: return null;
    }
  };
  const orig = useMemo(() => toForm(row), [row]);
  const [form, setForm] = useState<Record<string, string>>(orig);
  const [confirmClose, setConfirmClose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(orig), [form, orig]);

  const save = async () => {
    // v0.89：新增已废弃（物料只来自 BOM），这里只剩"保存补充字段"一条路。
    if (!row) return;
    setBusy(true);
    try {
      // 只提交 **BOM 没提供** 的字段。BOM 有值的不提交 ——
      // 一来后端会 `skipped` 掉，二来提交了也只是让它白跑一遍。
      const fields: Record<string, string> = {};
      for (const f of PM_ALL_FIELDS) {
        if (isBomLocked(f)) continue;
        fields[f] = form[f] ?? '';
      }
      const r = await savePartFields(row.internal_no ?? '', fields);
      onToast(r.updated || r.cleared
        ? `已保存补充字段（改 ${r.updated} 项 / 清 ${r.cleared} 项）`
        : '没有需要保存的改动', 'success');
      onSaved();
    } catch (e: any) {
      onToast(`保存失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (isNew) return;
    // ★ v0.57：删这一行会**连带删除它名下的图纸**（连同识别特征与 SIP 草稿），
    //   所以这里必须先说清楚再动手。原来没有二次确认 —— 那时删除的后果只是
    //   「解绑」（图纸还在，最坏是重新归户），而现在图纸是真的会从图纸工作台
    //   与作业指导书里消失（虽然能从回收站整批捞回来）。破坏性变了，
    //   确认这一步就得补上；数量用 `drawing_count`（该行名下全部图纸都会跟着走）。
    const n = row!.drawing_count ?? 0;
    const msg = n > 0
      ? `删除「${partLabel(row!)}」？\n\n`
        + `它名下的 ${n} 张图纸会一并删除（连同识别特征与作业指导书草稿）。\n`
        + `删除后可在「回收站」把这一条整批恢复。`
      : `删除「${partLabel(row!)}」？\n\n删除后可在「回收站」恢复。`;
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const res = await deletePartMaster(row!.id);
      // 如实报数：三个数都取后端返回值，**不许写死** —— 删 0 张和删 3 张是两种事，
      // 用户必须当场知道图纸工作台那边少了什么、去哪儿捞回来。
      const side: string[] = [];
      if (res.deleted_drawings) side.push(`${res.deleted_drawings} 张图纸一并删除`);
      if (res.deleted_bom_edges) side.push(`${res.deleted_bom_edges} 条 BOM 关系已解除`);
      onToast(side.length
        ? `已删除 1 个零件，${side.join('，')} —— 可在「回收站」整批恢复`
        : '已删除 1 个零件 —— 可在「回收站」恢复', 'success');
      onSaved();
    } catch (e: any) { onToast(e.message, 'error'); } finally { setBusy(false); }
  };

  const tryClose = () => { dirty ? setConfirmClose(true) : onClose(); };

  /** §② 的标题：`D21063901 · 铰链脚`（图号 + 名称），缺一个就退到有的那个。
   *  原先只显示 `零件 #4` —— 编辑面板里最该一眼看到的是"这是哪个零件"。 */
  const title = isNew ? '新增零件' : (
    [row!.part_no, row!.part_name].filter(Boolean).join(' · ')
    || `零件 #${row!.id}`);

  /**
   * 表单体的宿主标签：只读时用 `fieldset`，`disabled` 才能**级联**到内部全部控件；
   * 可编辑时仍是原来的 `div`（不改 DOM 结构 ⇒ 既有样式与前端回归门都不受影响）。
   * ⚠️ 类型写 `any` 是刻意的：`div` 没有 `disabled` 属性，写严格类型就得两套分支。
   */
  /** v0.89（沈哥 2026-10-22「把 BOM 系统没有的字段开放出来可编辑」）：
   *  **字段级**只读判定 —— 只有「BOM 确实提供了值」的字段才锁。
   *  ⚠️ 原来靠 `<fieldset disabled>` 一刀切，会把该补的字段也锁死；
   *     `source[k] === 'bom'` 才是"这个值来自 BOM、改了也白改"的准确判据。 */
  const isBomLocked = (k: string): boolean =>
    !isNew && (row?.source?.[k] as string | undefined) === 'bom';

  /** §② 的「自动 7 · 文件名 1 · 手填 7 · 空 1」—— 点开就知道哪些还得补。 */
  const srcStats = useMemo(() => {
    const c: Record<string, number> = {};
    for (const k of PM_ALL_FIELDS) {
      const s = (row?.source?.[String(k)] as string) || 'empty';
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [row]);

  return (
    <div className="pm-drawer-mask" onClick={tryClose}>
      <div className="pm-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="pm-drawer-head">
          <div>
            <h3>{title}</h3>
            <div className="pm-drawer-sub">
              {!isNew && (
                <span className="pm-srcstat">
                  {(['auto', 'filename', 'manual', 'empty'] as const)
                    .filter((k) => srcStats[k])
                    // v0.88：数据源早就是 BOM 了，「识别 / 手填 / 空」这套词
                    // 是台账时代的叙事。这里只保留一个有用的数：BOM 没提供的项数。
                    .map((k) => `${k === 'empty' ? 'BOM 缺' : PM_SOURCE_CN[k]} ${srcStats[k]}`)
                    .join(' · ')}
                  {'　'}
                </span>
              )}
              物料信息来自 <b>BOM 平台</b>（每天 03:10 同步）——
              <b>BOM 有值的字段只读</b>，BOM 没提供的可以直接补
              （存在本系统覆盖层，不会被每日同步冲掉）。
            </div>
          </div>
          <div className="pm-drawer-headright">
            {dirty && <span className="pm-dirty">● 有未保存改动</span>}
            <button className="pm-x" onClick={tryClose} aria-label="关闭">×</button>
          </div>
        </div>

        {/* v0.47：只读角色把整个表单体包进 `<fieldset disabled>` ——
            这是**一次性**关掉内部全部控件（输入框 / 下拉 / BOM 增删按钮 / 关联按钮）的办法，
            不必逐个去加 `disabled`（逐个加，将来新增一个字段忘了加就是一个口子）。
            `fieldset[disabled]` 连键盘 Tab 进不去都管住了。
            ⚠️ 用变量标签而不是 `<div disabled>`：后者 React 会警告未知属性。
            底部的「取消/保存」在 fieldset **之外**，不受影响。 */}
        {/* v0.89：**不再整体 `<fieldset disabled>`** —— 现在要「BOM 有值只读、
            BOM 没提供可编辑」，一刀切禁用会把该补的字段一起锁死。
            权限收敛到字段级（`isBomLocked`），逐个字段决定渲染成文本还是输入框。 */}
        <div className="pm-drawer-body">
          {PM_FIELD_GROUPS.map(([group, keys]) => (
            <section key={group} className="pm-group">
              <div className="pm-group-title">{group}</div>
              <div className="pm-fields">
                {keys.map((k) => (
                  <label key={String(k)} className="pm-field">
                    <span className="pm-label">
                      {PM_FIELD_CN[String(k)]}
                      {PM_BIZ_FIELDS.includes(String(k)) && <em className="pm-key" title="唯一键的组成列">键</em>}
                      {!isNew && (row!.source?.[String(k)] ?
                        <em className={`pm-badge ${row!.source[String(k)]}`}>
                          {PM_SOURCE_CN[row!.source[String(k)]] ?? row!.source[String(k)]}
                        </em> : null)}
                    </span>
                    {(() => {
                      const key = String(k);
                      // ★ v0.89：**只有 BOM 提供了值的字段**才渲染成只读纯文本；
                      //   BOM 没提供的（这轮实测有 project_type / project_name /
                      //   mold_type / cavity_count / drawing_rev / material_supplier
                      //   等 6 项，外加 BOM 侧留空的项）一律给输入框让人补。
                      //   —— 这正是沈哥 2026-10-22 要的「把 BOM 没有的字段开放出来」。
                      if (isBomLocked(key)) {
                        const v = form[key] ?? '';
                        return (
                          /* ⚠️ **不套 `.pm-input`**：那个类带固定 `height: 30px`，
                             而 div 里的长文本会换行 ⇒ 换行后就溢出到框外、压住右边一列
                             （沈哥 2026-10-22 截图：「很多字都超出了边界」）。
                             这里改成单行省略 + `title` 兜底（悬停看全值）。 */
                          <div title={v || undefined} style={{
                            minHeight: 26, padding: '4px 2px', fontSize: 13,
                            lineHeight: '18px', minWidth: 0,
                            color: v ? '#1f2937' : '#9ca3af',
                            whiteSpace: 'nowrap', overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {v || '—'}
                          </div>
                        );
                      }
                      const opts = optionOptsOf(key);
                      // 可编辑字段的占位提示：区分「人工补过的」和「BOM 压根没给」
                      const ph = row?.source?.[key] === 'manual'
                        ? '人工补充（可改）' : 'BOM 未提供，可补';
                      // 不在这 5 项里的字段保持原样的自由文本输入
                      if (opts === null) {
                        return (
                          <input className="pm-input" value={form[key] ?? ''}
                                 placeholder={ph}
                                 onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                        );
                      }
                      return (
                        <OptionSelect className="pm-input"
                                      opts={opts}
                                      value={form[key] ?? ''}
                                      disabled={ro}
                                      emptyHint="清单为空 —— 到「设置 · 量具与选项」里先加一项"
                                      onChange={(nv) => setForm({ ...form, [key]: nv })} />
                      );
                    })()}
                  </label>
                ))}
              </div>
            </section>
          ))}
          {/* v0.41 BOM 两栏（用户 2026-10-11：「总成、总成下面还有子零件」）。
              ⚠️ 放在「关联图纸」**之前** —— 结构（它是什么、它下面有什么）比
                 内容（它有几张图）更靠上层。
              ★ 两栏都**只建立/解除关系**：候选来自已有台账行（`listPartMaster`），
                这里**没有任何建零件的入口**（与「关联图纸不能上传」同一条原则）。 */}
          {/* v0.88：这三块原来查的是 `part_master.id`（已废弃）⇒ 永远「加载中…」。
              现在一律按**物料编码**查 BOM 镜像，且**只读** ——
              BOM 结构的唯一来源是 BOM 平台，这里能编辑就会长出第二套结构。 */}
          <section className="pm-group">
            <div className="pm-group-title">子零件（BOM · 只读）</div>
            <BomChildrenInline materialCode={row?.internal_no ?? ''} />
          </section>

          <section className="pm-group">
            <div className="pm-group-title">所属总成（只读）</div>
            <BomParentsInline materialCode={row?.internal_no ?? ''} />
          </section>

          {/* 「关联图纸」——预览稿 §② 的那一栏。
              ★ 候选全部来自图纸工作台**已有**图纸（`GET /api/drawings`），
                这里**没有任何上传入口**：台账只引用图纸，不产生图纸。
                （用户 2026-10-10：「不应该上传图纸欧」。） */}
          <section className="pm-group">
            <div className="pm-group-title">关联图纸</div>
            <PartDrawingsInline materialCode={row?.internal_no ?? ''} />
          </section>

          {/* 「识别到的文字」（v0.42，用户 2026-10-11：「导入图纸识别到的内容
              需要在主数据界面体现」，口径：「先把 OCR 原文列给我看」）。
              ★ 这里**只摆原文**：不自动填上面任何一格。识别值可能是错的，
                自动填下去 = "有把握的错值"，比留空更危险 —— 先看证据再填。
              ★ 放在「关联图纸」之后：它是**某一张图纸**的属性，先选图再看字。 */}
          <section className="pm-group">
            <div className="pm-group-title">识别到的文字</div>
            {isNew ? (
              <div className="pm-rel-note">保存并关联图纸后，这里会列出该图识别时读到的文字原文。</div>
            ) : (
              <RecognizedTextsPanel pmId={row!.id} currentDrawingId={currentDrawingId} />
            )}
          </section>

          <section className="pm-group">
            <div className="pm-group-title">备注</div>
            <textarea className="pm-textarea" rows={2} value={form.note ?? ''}
                      onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </section>
          <div className="pm-keysnote">
            唯一键 = {PM_BIZ_FIELDS.map((k) => PM_FIELD_CN[k]).join(' + ')}
            （比对前自动归一大小写与前后空格）。同一零件的不同版本请改「图纸版本」，不要另建一行。
          </div>
        </div>

        {/* §⑥-5：底部有「取消」，且在「保存」左侧。
            v0.47：只读角色只留「关闭」—— 删除与保存按钮**根本不渲染**。 */}
        <div className="pm-drawer-foot">
          {!isNew && !ro && (
            <button className="btn danger" disabled={busy} onClick={remove}>删除</button>
          )}
          {/* v0.88：这不是"角色权限不够" —— 物料本身就不在本系统维护。
              原来写「只读角色：仅可查看」，管理员看了会以为自己权限丢了。 */}
          {/* v0.89：文案要跟新规则一致 —— 不是"整页只读"，而是"BOM 有的只读、没给的可补"。 */}
          {ro && !dirty && (
            <span className="pm-dirty" style={{ color: '#5f5e5a' }}
                  title="BOM 提供的字段只读（它是权威，改了也会被每日同步覆盖）；
                          BOM 没提供的字段可以直接填，存在本系统的覆盖层里，不会被同步冲掉。">
              BOM 有的只读 · 空的可补
            </span>
          )}
          <div className="pm-foot-right">
            <button className="btn" disabled={busy} onClick={tryClose}>关闭</button>
            {/* v0.89：保存按钮不再受 `ro` 限制 —— 补字段是合法写操作。 */}
            {!isNew && (
              <button className="btn primary" disabled={busy || !dirty} onClick={save}
                      title={dirty ? '保存补齐的字段' : '没有改动'}>
                {busy ? '保存中…' : '保存补充字段'}
              </button>
            )}
          </div>
        </div>
      </div>

      {confirmClose && (
        <div className="pm-modal-mask" onClick={(e) => e.stopPropagation()}>
          <div className="pm-modal">
            <h4>有未保存的改动</h4>
            <p>直接关闭会丢掉刚才填的内容。</p>
            <div className="pm-modal-foot">
              <button className="btn" onClick={() => setConfirmClose(false)}>继续编辑</button>
              <button className="btn" onClick={() => { setConfirmClose(false); onClose(); }}>放弃改动</button>
              <button className="btn primary" disabled={busy}
                      onClick={async () => { setConfirmClose(false); await save(); }}>保存并关闭</button>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && !isNew && (
        <PickerDrawings pmId={row!.id} onClose={() => setPickerOpen(false)} onToast={onToast} />
      )}
    </div>
  );
}

// ================================================== 关联图纸（§②）
/** 该零件**已归户**的图纸清单 + 「+ 关联」。解除只断关系，绝不删图纸。 */
function RelatedDrawings({ pmId, currentDrawingId, onPick, onToast }: {
  pmId: number; currentDrawingId: number | null;
  onPick: () => void; onToast: Toast;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['pm-drawings', pmId],
    queryFn: () => listPartMasterDrawings(pmId),
  });
  const list = q.data ?? [];

  const unbind = async (d: DrawingSummary) => {
    setBusy(d.id);
    try {
      await bindDrawingPartMaster(d.id, null);
      onToast(`已解除关联：${d.filename}（图纸本身保留）`, 'success');
      // v0.56：解除关联会改「这张图归谁」⇒ 走统一入口（SIP 表头、图纸区
      // 的零件栏、台账图纸数全都跟着变）。
      invalidateDrawingGraph(qc);
    } catch (e: any) { onToast(e.message, 'error'); } finally { setBusy(null); }
  };

  return (
    <div className="pm-rel">
      {q.isLoading && <span className="pm-rel-note">加载中…</span>}
      {!q.isLoading && list.length === 0 && (
        <span className="pm-rel-note">还没有关联图纸。</span>
      )}
      {list.map((d) => (
        <span key={d.id} className="pm-rel-item">
          <span className="pm-rel-name" title={`#${d.id} ${d.filename}`}>{d.filename}</span>
          {d.id === currentDrawingId && <em className="pm-rel-cur">当前</em>}
          <button className="pm-rel-x" disabled={busy === d.id}
                  title="解除关联（只解除关系，不删图纸）"
                  onClick={() => unbind(d)}>解除</button>
        </span>
      ))}
      <button className="btn sm" onClick={onPick} title="从已有的图纸里挑选">+ 关联</button>
    </div>
  );
}

/**
 * 「识别到的文字」（v0.42）—— 该零件名下某张图纸**本次识别读到的原文**。
 *
 * 需求：2026-10-11「导入图纸识别到的内容需要在主数据界面体现」。
 * 口径（用户选定）：「**先把 OCR 原文列给我看**」。
 *
 * ★ 三件事**刻意不做**：
 *   ① **不自动填** 16 项里任何一格 —— 识别值可能是错的，自动填下去就是
 *      "有把握的错值"，比留空更危险。先看证据，再决定填什么。
 *   ② **不改文字**（不 trim、不纠错）—— 洗过的文本会掩盖识别错误，
 *      核对的价值就没了（OCR 读成 `D21O63502` 就该照原样看到）。
 *   ③ **不做"智能匹配"**：不猜哪一段对应哪一项。猜错比不猜更难发现。
 *
 * ★ `recorded === false` 必须**显式告诉用户**：这张图识别于 v0.42 之前，
 *   当时不记录文字，`total=0` 不代表"没读到"。没有这句话，用户看到 "0 段"
 *   会以为识别坏了 —— 而它其实只是老数据，重新识别一次就有。
 *   （铁律：「永远是 0 的诊断」与「已经死掉的诊断」必须能分辨。）
 *
 * ⚠️ 文本层（`vector`）与 OCR（`ocr`）的**坐标单位不同**（pt / px），
 *    所以坐标只放在 `title` 里给人核对，**不画在同一张图上**。
 */
function RecognizedTextsPanel({ pmId, currentDrawingId }: {
  pmId: number; currentDrawingId: number | null;
}) {
  const dq = useQuery({
    queryKey: ['pm-drawings', pmId],
    queryFn: () => listPartMasterDrawings(pmId),
  });
  const drawings = dq.data ?? [];
  const [pickId, setPickId] = useState<number | null>(null);
  const [kw, setKw] = useState('');
  const [src, setSrc] = useState<'' | 'vector' | 'ocr'>('');
  const [copied, setCopied] = useState(false);

  /** 看哪一张：优先用户在下拉里选的，其次「当前打开的那张」（若属于本零件），
   *  最后退回第一张。三者都不成立才为 null（没有关联图纸）。 */
  const effId = useMemo(() => {
    const has = (id: number | null) =>
      id != null && drawings.some((d) => d.id === id);
    if (has(pickId)) return pickId as number;
    if (has(currentDrawingId)) return currentDrawingId as number;
    return drawings.length ? drawings[0].id : null;
  }, [pickId, drawings, currentDrawingId]);

  // 一次取全（limit 兜住极端大图）：`total/vector/ocr` 要保持**筛选前**的口径，
  // 所以过滤放在本地做 —— 每敲一个字就请求一次既慢，又会让计数跟着抖动。
  const tq = useQuery({
    queryKey: ['drawing-texts', effId],
    queryFn: () => fetchDrawingTexts(effId as number, { limit: 2000 }),
    enabled: effId != null,
  });
  const data = tq.data;
  const all = data?.items ?? [];

  const shown = useMemo(() => {
    const k = kw.trim().toLowerCase();
    return all.filter((it) => {
      if (src && (it.source ?? '') !== src) return false;
      if (k && !(it.text ?? '').toLowerCase().includes(k)) return false;
      return true;
    });
  }, [all, kw, src]);

  const copyAll = async () => {
    const txt = shown.map((it) => it.text).join('\n');
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // 剪贴板被浏览器策略挡住时**不静默失败**：用户以为复制成功了更糟
      setCopied(false);
      window.prompt('复制失败，请手动复制：', txt);
    }
  };

  if (dq.isLoading) return <div className="pm-rel-note">加载中…</div>;
  if (drawings.length === 0) {
    return (
      <div className="pm-rel-note">
        这个零件还没有关联图纸 —— 先在上面「关联图纸」里挂一张，再看它识别到了什么。
      </div>
    );
  }

  return (
    <div className="pm-texts">
      {drawings.length > 1 && (
        <div className="pm-texts-pick">
          <span className="pm-label">看哪一张</span>
          <select className="pm-input" value={effId ?? ''}
                  onChange={(e) => setPickId(Number(e.target.value))}>
            {drawings.map((d) => (
              <option key={d.id} value={d.id}>
                {d.filename}{d.id === currentDrawingId ? '（当前打开）' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {tq.isLoading && <div className="pm-rel-note">正在读识别文字…</div>}
      {tq.isError && (
        <div className="pm-rel-note err">
          读不到识别文字：{(tq.error as any)?.message ?? '未知错误'}
        </div>
      )}

      {data && (
        <>
          {!data.recorded ? (
            // ★ 老数据：不能说"没读到"，只能说"当时没记"
            <div className="pm-texts-warn">
              这张图是在 v0.42 之前识别的，那时<b>还不记录</b>识别文字，
              所以这里看不到内容 —— <b>不代表没读到</b>。
              把它重新识别一次就能看到了。
            </div>
          ) : data.total === 0 ? (
            <div className="pm-texts-warn">
              本次识别<b>确实一段文字都没读到</b>（记录器工作正常，
              这张图上是真的没有可读文字）。
            </div>
          ) : null}

          <div className="pm-texts-bar">
            <span className="pm-texts-count">
              共 <b>{data.total}</b> 段
              <span className="pm-texts-sp">
                　文本层 {data.vector} · OCR {data.ocr}
                {data.page_no != null ? `　（第 ${data.page_no + 1} 页）` : ''}
              </span>
            </span>
            <div className="pm-texts-tools">
              <select className="pm-input sm" value={src}
                      onChange={(e) => setSrc(e.target.value as any)}
                      title="按来源筛选（文本层 = 图纸自带的文字；OCR = 从图像认出来的）">
                <option value="">全部来源</option>
                <option value="vector">文本层</option>
                <option value="ocr">OCR</option>
              </select>
              <input className="pm-input sm" placeholder="在原文里搜…"
                     value={kw} onChange={(e) => setKw(e.target.value)} />
              <button className="btn sm" onClick={copyAll}
                      disabled={shown.length === 0}
                      title="把当前列出的原文按行复制（可直接粘进 Excel）">
                {copied ? '已复制' : '复制原文'}
              </button>
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="pm-rel-note">
              {all.length === 0 ? '没有可显示的文字。' : '没有匹配的文字。'}
            </div>
          ) : (
            <ol className="pm-texts-list">
              {shown.map((it) => (
                <li key={it.id} className="pm-texts-item">
                  <span className="pm-texts-txt" title={coordTitle(it)}>{it.text}</span>
                  <em className={`pm-texts-src ${it.source ?? 'na'}`}
                      title={it.source === 'vector'
                        ? '来自图纸自带的文本层（不是 OCR 认出来的）'
                        : '来自图像 OCR'}>
                    {it.source === 'vector' ? '文本层' : it.source === 'ocr' ? 'OCR' : '来源未知'}
                  </em>
                </li>
              ))}
            </ol>
          )}

          {shown.length < data.total && (
            <div className="pm-texts-foot">
              只列出了 {shown.length} 段（其余被筛选或超出显示上限隐藏）。
              上方的「共 N 段」始终是本图的真实总数。
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** 坐标提示串。
 *
 *  ⚠️ 逐个判 `!= null`：`0` 是**合法坐标**，用 `x0 || '—'` 会把 0 显示成"—"，
 *     看起来像"这段没有坐标"，其实它就在页角上。
 */
function coordTitle(it: { x0: number | null; y0: number | null;
                          x1: number | null; y1: number | null;
                          source: 'vector' | 'ocr' | null;
                          confidence: number | null }): string {
  const n = (v: number | null) => (v == null ? '?' : String(Math.round(v * 10) / 10));
  const unit = it.source === 'vector' ? 'pt（PDF 点）' : 'px（原图像素）';
  const cf = it.confidence == null ? '' : `　置信度 ${it.confidence.toFixed(2)}`;
  return `位置 ${n(it.x0)},${n(it.y0)} → ${n(it.x1)},${n(it.y1)}　单位 ${unit}${cf}`;
}

/** 「+ 关联」选择器：**只列图纸工作台已有的图纸**，没有任何上传入口。 */
function PickerDrawings({ pmId, onClose, onToast }: {
  pmId: number; onClose: () => void; onToast: Toast;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const drawQ = useQuery({ queryKey: ['drawings'], queryFn: listDrawings });
  // 已归户到**别的**零件时，把归属名字显出来 —— 否则用户会以为"点了就多一份"
  const partQ = useQuery({ queryKey: ['part-master', ''], queryFn: () => listPartMaster() });
  const owner = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of partQ.data ?? []) m.set(r.id, r.part_no || r.part_name || `#${r.id}`);
    return m;
  }, [partQ.data]);

  const linkedIds = useQuery({
    queryKey: ['pm-drawings', pmId],
    queryFn: () => listPartMasterDrawings(pmId),
  }).data?.map((d) => d.id) ?? [];
  const linked = useMemo(() => new Set(linkedIds), [linkedIds]);

  const all = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (drawQ.data ?? []).filter((d) => !k || d.filename.toLowerCase().includes(k));
  }, [drawQ.data, q]);

  const toggle = (id: number) => setSel((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const moving = all.filter(
    (d) => sel.has(d.id) && d.part_master_id != null && !linked.has(d.id)).length;

  const apply = async () => {
    setBusy(true);
    let ok = 0; let firstErr = '';
    for (const id of sel) {
      try { await bindDrawingPartMaster(id, pmId); ok++; }
      catch (e: any) { if (!firstErr) firstErr = `${id}: ${e.message}`; }
    }
    setBusy(false);
    // v0.56：关联图纸 = 「零件主数据那里的文件增减」—— 走统一入口，
    // 否则切到 SIP 会先渲染 30 秒内的旧缓存（用户报的"没同步"就是这个）。
    invalidateDrawingGraph(qc);
    if (firstErr) onToast(`已关联 ${ok} 张，其余失败：${firstErr}`, 'error');
    else onToast(`已关联 ${ok} 张图纸`, 'success');
    onClose();
  };

  return (
    <div className="pm-modal-mask" onClick={onClose}>
      <div className="pm-modal pm-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h4>关联图纸（从已有图纸里挑）</h4>
        <p className="pm-pick-note">
          只列<strong>已经上传</strong>的图纸；这里不会新建、也不会上传图纸。
          一张图纸同时只能属于一个零件，改选即改归属。
        </p>
        <input className="pm-input" value={q} placeholder="搜索文件名…"
               onChange={(e) => setQ(e.target.value)} autoFocus />
        <div className="pm-pick-list">
          {drawQ.isLoading && <div className="pm-rel-note">加载中…</div>}
          {!drawQ.isLoading && all.length === 0 && (
            <div className="pm-rel-note">
              没有可选的图纸。请先用右上「上传并分析」传一张图纸，再回来关联。
            </div>
          )}
          {all.map((d) => {
            const isLinked = linked.has(d.id);
            const own = d.part_master_id != null && !isLinked
              ? (owner.get(d.part_master_id) ?? `#${d.part_master_id}`) : null;
            return (
              <label key={d.id} className={`pm-pick-row${isLinked ? ' on' : ''}`}>
                <input type="checkbox" checked={sel.has(d.id)} disabled={isLinked}
                       onChange={() => toggle(d.id)} />
                <span className="pm-pick-name" title={`#${d.id} ${d.filename}`}>{d.filename}</span>
                {isLinked && <em className="pm-pick-tag">已关联</em>}
                {own && <em className="pm-pick-tag warn">现属 {own}（将改归属）</em>}
              </label>
            );
          })}
        </div>
        <div className="pm-modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" disabled={busy || sel.size === 0} onClick={apply}>
            {busy ? '关联中…' : `关联所选 ${sel.size} 张${moving ? `（${moving} 张改归属）` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================ 批量：删除
function BulkDelete({ selRows, onDone, onToast }: {
  selRows: PartMasterRow[]; onDone: () => void; onToast: Toast;
}) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  // v0.57：这次动作会连带删掉几张图纸 —— 从当前列表行现算（`drawing_count` 是
  // 服务端给的该行名下图纸数，删除时**全部**跟着走）。放在确认框里先讲清楚，
  // 否则用户点完才发现图纸工作台少了一堆。真实张数仍以删除后的 toast 为准。
  const willDelete = selRows.reduce((n, r) => n + (r.drawing_count ?? 0), 0);
  const go = async () => {
    setBusy(true);
    try {
      const res = await bulkDeletePartMaster(selRows.map((r) => r.id));
      const side: string[] = [];
      if (res.deleted_drawings) side.push(`${res.deleted_drawings} 张图纸一并删除`);
      if (res.deleted_bom_edges) side.push(`${res.deleted_bom_edges} 条 BOM 关系已解除`);
      onToast(`已删除 ${res.deleted} 个零件`
        + (side.length ? `，${side.join('，')}` : '')
        + ' —— 可在「回收站」整批恢复',
        'success');
      setAsk(false); onDone();
    } catch (e: any) { onToast(e.message, 'error'); } finally { setBusy(false); }
  };
  return (
    <>
      <button className="btn sm danger" onClick={() => setAsk(true)}>批量删除</button>
      {ask && (
        <div className="pm-modal-mask" onClick={() => setAsk(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <h4>确认删除 {selRows.length} 个零件？</h4>
            <div className="pm-modal-list">
              {selRows.slice(0, 8).map((r) => (
                <div key={r.id}>
                  {r.part_no || <span className="pm-nil">（无零件编号）</span>}
                  {r.part_name ? ` — ${r.part_name}` : ''}
                  {r.drawing_rev ? ` （rev ${r.drawing_rev}）` : ''}
                </div>
              ))}
              {selRows.length > 8 && <div className="pm-nil">…还有 {selRows.length - 8} 条</div>}
            </div>
            <p>这些零件<strong>名下的图纸会一并删除</strong>
              {willDelete > 0 && <>（共 <strong>{willDelete} 张</strong>）</>}
              ，连同识别特征与作业指导书草稿；删除后可在「回收站」
              <strong>整批恢复</strong>。它们身上的 <strong>BOM 关系</strong>也会一并解除，
              <strong>对端零件不受影响</strong>（删的是关系，不是实物）。</p>
            <div className="pm-modal-foot">
              <button className="btn" onClick={() => setAsk(false)}>取消</button>
              <button className="btn danger" disabled={busy} onClick={go}>
                {busy ? '删除中…' : `确认删除 ${selRows.length} 个`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ================================================== BOM 父子关系（v0.41）
/** 台账行 / 边的可读名：`D21063502 · 铰链脚`（缺一个退到有的那个，都空则 `零件 #id`）。
 *
 *  ⚠️ 与后端 `part_master_service._label` **同一口径** —— 那边用于报错提示，
 *     这边用于界面显示。两处给出不同的名字，会让「提示里说的」和「列表里看的」
 *     对不上（用户只能自己猜是哪一个）。
 */
function partLabel(x: { part_no?: string | null; part_name?: string | null;
                       id?: number; other_id?: number }): string {
  const s = [x.part_no, x.part_name].filter(Boolean).join(' · ');
  return s || `零件 #${x.other_id ?? x.id}`;
}

/** 展开区里的**子零件行**（v0.48）：按主表的可见列逐行渲染，支持多级递归。
 *
 *  ★ 用户 2026-10-13 口径：「子零件能不能在主零件下显示**标题的那些信息**，
 *    就不要单独占用主零件行了」——「标题」= 列表表头，所以这里渲染的列
 *    就是 `cols`（= `visibleCols`，跟着工具条上的「列」开关走）。
 *    ⇒ 用户在列表里勾了「客户 / 材料牌号」，展开子零件时也能看到这两列。
 *
 *  ★ 数据取 `/children?with_row=true`：摘要字段只有编号 / 名称 / 内部编号，
 *    其余列全在 `edge.row` 上（见 `BomEdge.row` 的注释）。
 *  ★ 惰性加载：`/children` 只在真正展开时请求一次（列表页不为每行预先拉）。
 *  ★ 这里刻意**不做「添加」**：添加在编辑面板的 `BomChildrenPanel` 里 ——
 *    两处都做就成了两份实现（本项目「改了没反应」的头号成因）。
 *  ★ **多级**：子件自己还有下级（`row.child_count > 0`）时给一个 `+`，
 *    展开后继续递归同一套渲染 —— 子零件被折叠之后，列表这里是唯一
 *    能一眼看全整棵树的地方（用户 v0.41 就提过「总成下面还有子零件」）。
 *    递归是安全的：后端 `add_child` 已有环路检测；前端另加 `depth` 上限兜底。
 *  ⚠️ 这些 `<tr>` **不是**台账行：不参与勾选 / 批量删除 / 导出
 *    （那三件事一律按 `id` 走，而共用件会在多个总成下各出现一次）。
 *  ⚠️ `queryKey` 用 `['pm-children-rows', pmId]`，**刻意与编辑面板的
 *    `['pm-children', pmId]` 分开** —— 那边只要摘要，这边要整行，
 *    共用一个 key 会互相污染（一个先到就决定了另一个拿到的形状）。
 */
/** v0.87：BOM 平台的**子件清单**（只读，替代恒空的老 `bom_edges` 展开）。
 *
 *  沈哥 2026-10-22：「气泡图零件主数据中有子项物料，需要从 BOM 平台导入」
 *
 *  ★ 只读是刻意的：BOM 结构的唯一来源是 BOM 平台，
 *    这里能编辑就会长出第二套结构，下次同步一跑就打架。
 *  ★ 子件可能是 A/E 类（原材料），不在列表的 P/B/D 范围里 —— 所以只显示
 *    编码 + 名称 + 用量，不提供"点进那一行"（点了也查不到）。
 */
function BomPlatformChildren({ materialCode, span }: {
  materialCode: string;
  span: number;
}) {
  const q = useQuery({
    queryKey: ['bom-children', materialCode],
    queryFn: () => fetchBomChildren(materialCode),
    enabled: !!materialCode,
    refetchOnWindowFocus: false,
  });
  const items = q.data?.items ?? [];
  if (!materialCode) return null;
  if (q.isLoading) {
    return (
      <tr><td colSpan={span}
              style={{ fontSize: 12, opacity: 0.6, padding: '3px 8px 3px 30px' }}>
        正在读 BOM 子件…
      </td></tr>
    );
  }
  if (items.length === 0) {
    return (
      <tr><td colSpan={span}
              style={{ fontSize: 12, opacity: 0.6, padding: '3px 8px 3px 30px' }}>
        BOM 里这个物料没有子件（或 BOM 侧未维护）
      </td></tr>
    );
  }
  const totalQty = items.reduce((n, x) => n + (x.qty ?? 0), 0);
  return (
    <tr>
      <td colSpan={span} style={{ background: '#f8fafc',
                                  padding: '5px 8px 6px 30px' }}>
        <div style={{ fontSize: 12, color: '#185fa5', marginBottom: 4 }}>
          BOM 子件 · {items.length} 项（来自 BOM 平台，只读）
          {totalQty ? <span style={{ opacity: 0.6 }}> ｜ 用量合计 {totalQty.toFixed(3)}</span> : null}
        </div>
        {items.map((it, i) => (
          <div key={`${it.child_code}_${it.seq_no}_${i}`}
               style={{ display: 'flex', gap: 10, fontSize: 12,
                        padding: '1px 0', alignItems: 'baseline' }}>
            <span style={{ width: 22, opacity: 0.45 }}>{it.seq_no ?? i + 1}</span>
            <span style={{ fontFamily: 'monospace', minWidth: 140 }}>
              {it.child_code}
            </span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                           textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.child_name || ''}
            </span>
            <span style={{ minWidth: 70, textAlign: 'right', opacity: 0.75 }}>
              {it.qty != null ? `× ${it.qty}` : ''}
            </span>
          </div>
        ))}
      </td>
    </tr>
  );
}

/** 只读关系列表的公共行（v0.88）。 */
function RelRow({ code, name, tail, note }: {
  code: string; name?: string | null; tail?: string; note?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                  padding: '2px 0', fontSize: 12.5, borderBottom: '1px dashed #eef2f7' }}>
      <span style={{ fontFamily: 'monospace', minWidth: 150 }}>{code}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                     textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                     color: name ? '#374151' : '#9ca3af' }}>
        {name || note || '—'}
      </span>
      {tail && <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>{tail}</span>}
    </div>
  );
}

function RelNote({ children }: { children: React.ReactNode }) {
  return <div className="pm-rel-note" style={{ padding: '2px 0' }}>{children}</div>;
}

/** 子零件（只读，来自 BOM）。 */
function BomChildrenInline({ materialCode }: { materialCode: string }) {
  const q = useQuery({
    queryKey: ['bom-children', materialCode],
    queryFn: () => fetchBomChildren(materialCode),
    enabled: !!materialCode,
    refetchOnWindowFocus: false,
  });
  if (!materialCode) return <RelNote>这个物料没有编码，无法查 BOM 子件。</RelNote>;
  if (q.isLoading) return <RelNote>正在读…</RelNote>;
  const items = q.data?.items ?? [];
  if (items.length === 0) return <RelNote>BOM 里这个物料没有子件（或 BOM 侧未维护）。</RelNote>;
  return (
    <div>
      {items.map((it, i) => (
        <RelRow key={`${it.child_code}_${i}`} code={it.child_code}
                name={it.child_name}
                tail={it.qty != null ? `× ${it.qty}` : ''} />
      ))}
    </div>
  );
}

/** 所属总成（只读，反查 BOM 结构）。 */
function BomParentsInline({ materialCode }: { materialCode: string }) {
  const q = useQuery({
    queryKey: ['bom-parents', materialCode],
    queryFn: () => fetchBomParents(materialCode),
    enabled: !!materialCode,
    refetchOnWindowFocus: false,
  });
  if (!materialCode) return <RelNote>这个物料没有编码，无法反查总成。</RelNote>;
  if (q.isLoading) return <RelNote>正在读…</RelNote>;
  const items = q.data?.items ?? [];
  if (items.length === 0) return <RelNote>它没有装在任何总成下面（或 BOM 侧未维护）。</RelNote>;
  return (
    <div>
      {items.map((it, i) => (
        <RelRow key={`${it.parent_code}_${i}`} code={it.parent_code}
                name={it.parent_name}
                tail={[it.qty != null ? `× ${it.qty}` : '', it.seq_no != null ? `seq ${it.seq_no}` : '']
                  .filter(Boolean).join(' · ')} />
      ))}
    </div>
  );
}

/** 关联图纸（只读，按 `drawings.part_no` 查）。 */
function PartDrawingsInline({ materialCode, onOpen }: {
  materialCode: string;
  onOpen?: (drawingId: number) => void;
}) {
  const q = useQuery({
    queryKey: ['part-drawings-by-code', materialCode],
    queryFn: () => fetchPartDrawings(materialCode),
    enabled: !!materialCode,
    refetchOnWindowFocus: false,
  });
  if (!materialCode) return <RelNote>这个物料没有编码，无法查关联图纸。</RelNote>;
  if (q.isLoading) return <RelNote>正在读…</RelNote>;
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return <RelNote>
      还没有图纸归户到这个物料 —— 在图纸工作台右上角「未归户」里挂一张。
    </RelNote>;
  }
  return (
    <div>
      {items.map((d) => (
        <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                                 padding: '2px 0', fontSize: 12.5,
                                 borderBottom: '1px dashed #eef2f7' }}>
          <span style={{ fontFamily: 'monospace', minWidth: 60 }}>#{d.id}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {d.filename}
          </span>
          <span style={{ opacity: 0.65 }}>{d.page_count ? `${d.page_count} 页` : ''}</span>
          {onOpen && (
            <button className="btn sm" onClick={() => onOpen(d.id)}>打开</button>
          )}
        </div>
      ))}
    </div>
  );
}

function BomChildRows({ pmId, depth, cols, expanded, onToggle, onJump,
                        onToast, onOpenDrawings }: {
  pmId: number;
  depth: number;
  cols: Array<{ key: keyof PartMasterRow; label: string }>;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onJump: (id: number) => void;
  onToast: Toast;
  /** ⚠️ 类型必须与调用方（`PartMasterPageContent` 的 props）**逐字一致**：
   *  它排除了 `{kind:'all'}`，因为组件本身永远不传那个分支。
   *  写成宽的 `DrawingScope` 会在 `tsc -b` 处直接拦住构建。 */
  onOpenDrawings: (scope: Exclude<DrawingScope, { kind: 'all' }>) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['pm-children-rows', pmId],
                       queryFn: () => listPartChildren(pmId, true) });
  const list = q.data ?? [];
  /** 勾选列 + 可见列 + 图纸列 + 箭头列 —— 与表头**逐格对齐**。 */
  const span = cols.length + 3;
  /** 编号列被用户关掉时的兜底：`part_no` 是"这一行是什么"的唯一标识，
   *  没有它就没法点开编辑面板，所以改挂在勾选列那格里（见下面的渲染）。 */
  const hasPartNoCol = cols.some((c) => String(c.key) === 'part_no');

  const unbind = async (e: BomEdge) => {
    setBusy(e.id);
    try {
      await removeBomEdge(e.id);
      onToast(`已解除：「${partLabel(e)}」不再挂在本总成下面（零件本身保留）—— 可在「回收站」恢复`, 'success');
      // ⚠️ 两个 key 都要刷：React Query 的 invalidate 是**前缀**匹配，
      //    `['pm-children']` **不会**命中 `['pm-children-rows', ...]`。
      qc.invalidateQueries({ queryKey: ['pm-children-rows'] });
      qc.invalidateQueries({ queryKey: ['pm-children'] });
      qc.invalidateQueries({ queryKey: ['pm-parents', e.other_id] });
      qc.invalidateQueries({ queryKey: ['part-master'] });
    } catch (err: any) { onToast(err.message, 'error'); } finally { setBusy(null); }
  };

  if (depth > 8) {
    // 数据有环时的兜底（后端建边时已拦环路，这里不该被触发）。
    // ⚠️ 宁可显示一行"太深了"，也不能无限递归把页面卡死。
    return (
      <tr className="pm-subrow"><td colSpan={span} className="pm-subrow-note">
        层级过深（超过 8 层）—— 疑似 BOM 数据成环，请到编辑面板里检查。
      </td></tr>
    );
  }
  if (q.isLoading) {
    return <tr className="pm-subrow"><td colSpan={span} className="pm-subrow-note">加载中…</td></tr>;
  }
  if (list.length === 0) {
    return (
      <tr className="pm-subrow"><td colSpan={span} className="pm-subrow-note">
        {depth === 1 ? (
          <>下面还没有子零件 —— 在编辑面板的「子零件（BOM）」栏里点「+ 添加子零件」，
            挑一个已有零件，或当场新建一个（<strong>不需要图纸</strong>）。</>
        ) : '它下面没有子零件。'}
      </td></tr>
    );
  }

  /** 编号格的内容：缩进 + 可点的编号 + 用量/位号小标。 */
  const noCell = (e: BomEdge) => (
    <>
      <span className="pm-subrow-indent" aria-hidden>{'└'}</span>
      <button className="pm-bom-name" title="打开这个子零件的编辑面板"
              onClick={(ev) => { ev.stopPropagation(); onJump(e.other_id); }}>
        {e.part_no || `零件 #${e.other_id}`}
      </button>
      {e.qty && <em className="pm-bom-tag">用量 {e.qty}</em>}
      {e.position && <em className="pm-bom-tag">位号 {e.position}</em>}
    </>
  );

  return (
    <>
      {list.map((e) => {
        const row = e.row ?? null;
        return (
          <Fragment key={e.id}>
            <tr className="pm-subrow" style={{ ['--pm-depth' as any]: depth }}>
              {/* 勾选列：留空 —— 子件行不参与勾选 / 批量删除 / 导出。
                  编号列被关掉时，编号改挂在这一格里（否则没法点开编辑面板）。 */}
              <td className="pm-checkcol pm-subrow-gutter">
                {!hasPartNoCol && noCell(e)}
              </td>
              {cols.map((c) => {
                const k = String(c.key);
                if (k === 'part_no') {
                  return <td key={k} className="pm-partno-cell">{noCell(e)}</td>;
                }
                const v = row ? (row as any)[c.key] : null;
                return (
                  <td key={k} title={v ?? ''} className={v ? undefined : 'pm-nil-cell'}>
                    {v || <span className="pm-nil">—</span>}
                  </td>
                );
              })}
              {/* 图纸列：与主表同款（可点钻取到图纸区）；v0.51 同样套 `.pm-dwg-wrap` 双签。 */}
              <td className="pm-dwgcol">
                <span className="pm-dwg-wrap">
                {(row?.drawing_count ?? 0) > 0
                  ? <button className="pm-dwg-n"
                            title={`查看这个子零件名下的 ${row!.drawing_count} 张图纸`}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              onOpenDrawings({
                                kind: 'part', id: e.other_id,
                                label: [row?.part_no, row?.part_name]
                                         .filter(Boolean).join(' · ') || `零件 #${e.other_id}`,
                              });
                            }}>
                      {row!.drawing_count} 张 ▸
                    </button>
                  : <span className="pm-dwg-none" title="这个子零件名下没有图纸。">无图纸</span>}
                </span>
              </td>
              {/* 箭头列：多级下钻 + 解除关系，都在这里。 */}
              <td className="pm-arrowcol pm-subrow-ops"
                  onClick={(ev) => ev.stopPropagation()}>
                {(row?.child_count ?? 0) > 0 && (
                  <button className={`pm-exp sm${expanded.has(e.other_id) ? ' on' : ''}`}
                          aria-expanded={expanded.has(e.other_id)}
                          title={`它自己还有 ${row!.child_count} 个子零件 —— 点开继续往下看`}
                          onClick={() => onToggle(e.other_id)}>
                    {expanded.has(e.other_id) ? '−' : '+'}
                  </button>
                )}
                <button className="pm-rel-x" disabled={busy === e.id}
                        title="解除父子关系（两个零件都保留）"
                        onClick={() => unbind(e)}>解除</button>
              </td>
            </tr>
            {row && expanded.has(row.id) && (
              <BomChildRows pmId={row.id} depth={depth + 1} cols={cols}
                            expanded={expanded} onToggle={onToggle} onJump={onJump}
                            onToast={onToast} onOpenDrawings={onOpenDrawings} />
            )}
          </Fragment>
        );
      })}
    </>
  );
}

/** 编辑面板里的「子零件（BOM）」：加 / 改用量位号 / 解除。
 *
 *  ★ 「总成」不是一个存下来的类型 —— 这一栏一个子件都没加时它就是普通零件；
 *    加上第一个子件，它在列表里的角色就变成总成（`child_count > 0`）。
 */
function BomChildrenPanel({ pmId, onToast }: { pmId: number; onToast: Toast }) {
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState<BomEdge | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['pm-children', pmId],
                       queryFn: () => listPartChildren(pmId) });
  const list = q.data ?? [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pm-children', pmId] });
    qc.invalidateQueries({ queryKey: ['part-master'] });
  };

  const unbind = async (e: BomEdge) => {
    setBusy(e.id);
    try {
      await removeBomEdge(e.id);
      onToast(`已解除：「${partLabel(e)}」不再挂在本总成下面（零件本身保留）—— 可在「回收站」恢复`, 'success');
      refresh();
      qc.invalidateQueries({ queryKey: ['pm-parents', e.other_id] });
    } catch (err: any) { onToast(err.message, 'error'); } finally { setBusy(null); }
  };

  return (
    <div className="pm-rel">
      {q.isLoading && <span className="pm-rel-note">加载中…</span>}
      {!q.isLoading && list.length === 0 && (
        <span className="pm-rel-note">还没有子零件。</span>
      )}
      {list.map((e) => (
        <span key={e.id} className="pm-rel-item">
          <span className="pm-rel-name" title={`零件 #${e.other_id}`}>{partLabel(e)}</span>
          {e.qty && <em className="pm-bom-tag">用量 {e.qty}</em>}
          {e.position && <em className="pm-bom-tag">位号 {e.position}</em>}
          <button className="pm-rel-edit" title="该这条关系的用量 / 位号 / 备注"
                  onClick={() => setEditing(e)}>用量/位号</button>
          <button className="pm-rel-x" disabled={busy === e.id}
                  title="解除父子关系（两个零件都保留）"
                  onClick={() => unbind(e)}>解除</button>
        </span>
      ))}
      <button className="btn sm" onClick={() => setPicking(true)}
              title="挂一个零件到它下面：可以从已有零件里挑，也可以当场新建一个（都不需要图纸）">
        + 添加子零件
      </button>

      {picking && (
        <PickerParts pmId={pmId} existing={list}
                     onClose={() => setPicking(false)} onDone={refresh} onToast={onToast} />
      )}
      {editing && (
        <EditEdgeModal edge={editing} onClose={() => setEditing(null)}
                       onDone={refresh} onToast={onToast} />
      )}
    </div>
  );
}

/** 编辑面板里的「所属总成」：这个零件装在哪些总成下面（含用量 / 位号）。
 *
 *  ★ **共用件会列出多条** —— 它被几个总成引用就有几条，用量/位号各算各的。
 *    这正是当初选边表而不是 `parent_id` 的理由（`parent_id` 一行只能一个父件）。
 */
function BomParentsPanel({ pmId, onToast }: { pmId: number; onToast: Toast }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['pm-parents', pmId],
                       queryFn: () => listPartParents(pmId) });
  const list = q.data ?? [];

  const unbind = async (e: BomEdge) => {
    setBusy(e.id);
    try {
      await removeBomEdge(e.id);
      onToast(`已解除：不再挂在「${partLabel(e)}」下面（零件本身保留）—— 可在「回收站」恢复`, 'success');
      qc.invalidateQueries({ queryKey: ['pm-parents', pmId] });
      qc.invalidateQueries({ queryKey: ['pm-children', e.other_id] });
      qc.invalidateQueries({ queryKey: ['part-master'] });
    } catch (err: any) { onToast(err.message, 'error'); } finally { setBusy(null); }
  };

  return (
    <div className="pm-rel">
      {q.isLoading && <span className="pm-rel-note">加载中…</span>}
      {!q.isLoading && list.length === 0 && (
        <span className="pm-rel-note">
          没有装在任何一个总成下面（它现在是顶层零件）。
        </span>
      )}
      {list.map((e) => (
        <span key={e.id} className="pm-rel-item">
          <span className="pm-rel-name" title={`零件 #${e.other_id}`}>{partLabel(e)}</span>
          {e.qty && <em className="pm-bom-tag">用量 {e.qty}</em>}
          {e.position && <em className="pm-bom-tag">位号 {e.position}</em>}
          <button className="pm-rel-x" disabled={busy === e.id}
                  title="从这个总成下面摘掉（两个零件都保留）"
                  onClick={() => unbind(e)}>解除</button>
        </span>
      ))}
      {list.length > 1 && (
        <span className="pm-bom-hint">
          共用件：同一个零件装在 {list.length} 个总成下面，各自的用量 / 位号互不影响。
        </span>
      )}
    </div>
  );
}

/** 「+ 添加子零件」选择器：**只列已经存在的台账行**，没有任何建零件的入口。
 *
 *  ★ 候选里会标出「已装于 N 个总成」—— 共用件是**允许**的（这正是边表的意义），
 *    所以不能拦；但必须让人看见，免得误以为"这零件重复了"。
 */
function PickerParts({ pmId, existing, onClose, onDone, onToast }: {
  pmId: number; existing: BomEdge[];
  onClose: () => void; onDone: () => void; onToast: Toast;
}) {
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<PartMasterRow | null>(null);
  const [qty, setQty] = useState('');
  const [pos, setPos] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** v0.44：是否切到「现场新建一个零件并挂上」的模式。 */
  const [creating, setCreating] = useState(false);
  /** v0.44：现场新建只收**照图能念出来**的 5 项；其余 11 项建完再双击那行去补。
   *  ⚠️ 不要在这里铺 16 项 —— 用户此刻的目的是"赶紧把子件挂上"，
   *     不是一个缩微版的编辑面板。 */
  const [nf, setNf] = useState({ part_no: '', part_name: '', internal_no: '',
                                 customer: '', material_grade: '' });

  const listQ = useQuery({ queryKey: ['part-master', ''], queryFn: () => listPartMaster() });
  const linked = useMemo(() => new Set(existing.map((e) => e.child_pm_id)), [existing]);
  const kw = q.trim().toLowerCase();
  const rows = (listQ.data ?? []).filter((r) =>
    r.id !== pmId && !linked.has(r.id) &&
    (!kw || [r.part_no, r.part_name, r.internal_no, r.customer]
      .some((v) => (v ?? '').toLowerCase().includes(kw))));

  const submit = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await addPartChild(pmId, {
        child_id: pick.id,
        qty: qty.trim() || null,
        position: pos.trim() || null,
        note: note.trim() || null,
      });
      onToast(`已添加子零件：${partLabel(pick)}`, 'success');
      onDone(); onClose();
    } catch (e: any) {
      // 后端把「自己挂自己 / 已经在里面 / 会成环」都转成了中文提示，直接用
      onToast(e.message, 'error');
    } finally { setBusy(false); }
  };

  /** v0.44（用户 2026-10-11：「如果没有图纸那我是不是没法建立子零件？手动输入子零件怎么建立」）：
   *  在**这一步**就把零件建出来并挂上 —— 不必先退回列表建好行、再回来挑。
   *
   *  ★ 先澄清一个容易误解的点：**台账行与图纸本来就是两回事**。
   *    `part_master` 行完全可以一张图纸都没有（列表里会标「无图纸」，
   *    那是**提示**不是错误：图纸目录删掉后行不删，正是 v0.39 定的口径）。
   *    所以「没图纸」从不是建子零件的障碍 —— 以前真正的障碍只是这个弹层里
   *    没有新建入口。
   *
   *  ★ 顺序必须是「先建行 → 再挂边」：`bom_edges` 的 `child_id` 指向的是
   *    台账行 id，没有行就没有可挂的东西。
   */
  const createAndAttach = async () => {
    const pn = nf.part_no.trim();
    if (!pn) { onToast('至少填「零件编号」', 'error'); return; }
    setBusy(true);
    try {
      // 复用模块级的 `toPayload`：**「空串 → null」这条纪律只有那一处**，
      // 这里再写一份就又是"同一语义两份实现"。
      const form: Record<string, string> = {};
      for (const k of [...PM_ALL_FIELDS, 'note']) form[k] = '';
      for (const [k, v] of Object.entries(nf)) form[k] = v;
      const row = await createPartMaster(toPayload(form));
      await addPartChild(pmId, {
        child_id: row.id,
        qty: qty.trim() || null,
        position: pos.trim() || null,
        note: note.trim() || null,
      });
      onToast(`已新建零件「${row.part_no ?? pn}」并挂到本总成`, 'success');
      onDone(); onClose();
    } catch (e: any) {
      // 「四键撞唯一键」后端会给中文提示（例如"这个编号已经建过一行"）——
      // 直接用它的话，不要自己编一套说法。
      onToast(e.message, 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="pm-modal-mask" onClick={onClose}>
      <div className="pm-modal pm-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h4>添加子零件</h4>
        <div className="pm-pick-note">
          {creating ? (
            <>
              这里新建的是一条<strong>零件台账行</strong>，建完立刻挂到本总成下面。
              台账行和图纸<strong>是两回事</strong>：<strong>没有图纸也能建</strong>
              —— 列表里那行会标「无图纸」，那是提示不是错误。
            </>
          ) : (
            <>
              只列<strong>已经存在</strong>的零件。要挂的零件还没建过？
              点下面的「<strong>+ 新建零件并挂上</strong>」当场建一行
              —— <strong>不需要图纸</strong>。
            </>
          )}
        </div>

        {creating ? (
          <div className="pm-newform">
            <div className="pm-edge-form">
              <label className="pm-field">
                <span className="pm-label">零件编号 <em className="pm-req">*</em></span>
                <input className="pm-input" value={nf.part_no} placeholder="如 ARI591-10X"
                       onChange={(e) => setNf({ ...nf, part_no: e.target.value })} />
              </label>
              <label className="pm-field">
                <span className="pm-label">零件名称</span>
                <input className="pm-input" value={nf.part_name}
                       onChange={(e) => setNf({ ...nf, part_name: e.target.value })} />
              </label>
              <label className="pm-field">
                <span className="pm-label">内部编号</span>
                <input className="pm-input" value={nf.internal_no}
                       onChange={(e) => setNf({ ...nf, internal_no: e.target.value })} />
              </label>
              <label className="pm-field">
                <span className="pm-label">客户</span>
                <input className="pm-input" value={nf.customer}
                       onChange={(e) => setNf({ ...nf, customer: e.target.value })} />
              </label>
              <label className="pm-field">
                <span className="pm-label">材料牌号</span>
                <input className="pm-input" value={nf.material_grade}
                       onChange={(e) => setNf({ ...nf, material_grade: e.target.value })} />
              </label>
            </div>
            <div className="pm-rel-note">
              填「零件编号」就能挂。其余 11 项以后双击这一行再补（编辑面板里是完整 16 项）。
              <br />
              台账唯一键是<strong>客户 + 内部编号 + 零件编号 + 图纸版本</strong>四列 ——
              这四列与已有行完全一样时会提示重复，换一列区分开就行。
            </div>
          </div>
        ) : (
          <>
            <input className="pm-search" value={q}
                   placeholder="搜索 图号 / 名称 / 内部编号 / 客户…"
                   onChange={(e) => setQ(e.target.value)} />
            <div className="pm-pick-list">
              {listQ.isLoading && <div className="pm-rel-note">加载中…</div>}
              {!listQ.isLoading && rows.length === 0 && (
                <div className="pm-rel-note">
                  没有可选的零件 —— 能挂的都已经挂上去了，或者搜索没有命中。
                </div>
              )}
              {rows.map((r) => (
                <label key={r.id} className="pm-pick-row">
                  <input type="radio" name="pm-child-pick" checked={pick?.id === r.id}
                         onChange={() => setPick(r)} />
                  <span className="pm-pick-name">{partLabel(r)}</span>
                  {r.internal_no && <em className="pm-pick-tag">{r.internal_no}</em>}
                  {r.parent_count > 0 && (
                    <em className="pm-pick-tag"
                        title="共用件是允许的：同一零件可以装在多个总成下，用量 / 位号各自独立">
                      已装于 {r.parent_count} 个总成
                    </em>
                  )}
                </label>
              ))}
            </div>
          </>
        )}
        {/* 用量 / 位号 / 备注：挑已有零件与现场新建**共用同一份表单** ——
            两套输入框就会两处口径（这里有、那里漏）。 */}
        {(pick || creating) && (
          <div className="pm-edge-form">
            <label className="pm-field">
              <span className="pm-label">用量</span>
              <input className="pm-input" value={qty} placeholder="如 2 / 1 SET"
                     onChange={(e) => setQty(e.target.value)} />
            </label>
            <label className="pm-field">
              <span className="pm-label">位号</span>
              <input className="pm-input" value={pos} placeholder="如 A3"
                     onChange={(e) => setPos(e.target.value)} />
            </label>
            <label className="pm-field">
              <span className="pm-label">备注</span>
              <input className="pm-input" value={note}
                     onChange={(e) => setNote(e.target.value)} />
            </label>
          </div>
        )}
        <div className="pm-modal-foot">
          {creating ? (
            <button className="btn pm-foot-l" onClick={() => setCreating(false)}>
              ← 回到已有零件
            </button>
          ) : (
            <button className="btn pm-foot-l"
                    title="建一条零件台账行并立刻挂到本总成下面（不需要图纸）"
                    onClick={() => { setPick(null); setCreating(true); }}>
              + 新建零件并挂上
            </button>
          )}
          <button className="btn" onClick={onClose}>取消</button>
          {creating ? (
            <button className="btn primary" disabled={busy} onClick={createAndAttach}>
              {busy ? '新建中…' : '新建并挂到本总成'}
            </button>
          ) : (
            <button className="btn primary" disabled={busy || !pick} onClick={submit}>
              {busy ? '添加中…' : pick ? `挂到本总成：${partLabel(pick)}` : '先选一个零件'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 改这一条 BOM 关系的用量 / 位号 / 备注（**改不了父子** —— 换父子 = 删旧建新）。 */
function EditEdgeModal({ edge, onClose, onDone, onToast }: {
  edge: BomEdge; onClose: () => void; onDone: () => void; onToast: Toast;
}) {
  const [qty, setQty] = useState(edge.qty ?? '');
  const [pos, setPos] = useState(edge.position ?? '');
  const [note, setNote] = useState(edge.note ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateBomEdge(edge.id, {
        qty: qty.trim() || null,
        position: pos.trim() || null,
        note: note.trim() || null,
      });
      onToast('已保存用量 / 位号', 'success');
      onDone(); onClose();
    } catch (e: any) { onToast(e.message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="pm-modal-mask" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <h4>用量 / 位号 —— {partLabel(edge)}</h4>
        <div className="pm-pick-note">
          改的是<strong>这一条关系</strong>，不是零件本身；父子关系不变
          （要换父子就解除后重挂）。
        </div>
        <div className="pm-edge-form">
          <label className="pm-field">
            <span className="pm-label">用量</span>
            <input className="pm-input" value={qty} placeholder="如 2 / 1 SET"
                   onChange={(e) => setQty(e.target.value)} />
          </label>
          <label className="pm-field">
            <span className="pm-label">位号</span>
            <input className="pm-input" value={pos} placeholder="如 A3"
                   onChange={(e) => setPos(e.target.value)} />
          </label>
          <label className="pm-field">
            <span className="pm-label">备注</span>
            <input className="pm-input" value={note}
                   onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        <p className="pm-nil">留空 = 清掉该项（不是"不改"）。</p>
        <div className="pm-modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" disabled={busy} onClick={save}>
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
